import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useWS } from '../contexts/WebSocketContext';
import { useMobileDetection } from '../hooks/useMobileDetection';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import PrimeAssistant from './PrimeAssistant';
import MobileBottomNav from './MobileBottomNav';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { LayoutDashboard, Users, Settings, LogOut, Moon, Sun, Menu, X, Eye, ShieldCheck, TrendingUp, Trello, ChevronDown, ChevronRight, ChevronLeft, BarChart3, Wrench, Megaphone, Grid, Palette, FileText, Image, Calendar, Target, Upload, Bell, Clock, LifeBuoy, MessageSquare, Ghost, Search, User, Headphones, Cpu, Shield, ArrowLeftRight, UserCog, Share2, Brain, Bot, Send, Sparkles, Loader2, Crown, Cog, Briefcase, ClipboardList, CheckSquare, Star, Phone, Trophy, MapPin, Rocket, DollarSign, Lock, Smartphone, Monitor, AlertCircle, Building, LayoutGrid, Info, Globe, Pin, PinOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import MediaHub from './MediaHub';
import { GhostProtocolModal } from './GhostProtocolModal';
import { RhinoOneBadge } from './RhinoOneBadge';
import { WorklogHeaderButton, WorklogModal } from './worklog/WorklogModal';
// Import navigation config - this is the source of truth for nav items
import { 
  PIPELINE_NAV_ITEMS, 
  PRODUCTION_NAV_ITEMS, 
  MARKETING_NAV_ITEMS,
  HUB_CONFIG,
  isNavItemActive,
  getActiveHubForPath
} from '../config/navigation.config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Master password for Ghost Protocol
const MASTER_PASSWORD = '050378';

// Define role-to-department mappings
const SALES_ROLES = ['sales', 'sales_manager', 'sales_rep', 'director_of_sales'];
const OPERATIONS_ROLES = ['operations', 'operations_manager', 'production_manager', 'production', 'field_technician', 'installer'];
const MARKETING_ROLES = ['marketing', 'marketing_manager'];
const ADMIN_ROLES = ['super_admin', 'admin', 'ai_assistant'];
const CALL_CENTER_ROLES = ['call_center', 'call_center_manager', 'call_center_agent'];
const BUSINESS_OPS_ROLES = ['business_ops'];

// Tester role - has broad access to test all user flows (NOT admin features)
const TESTER_ROLES = ['tester'];

// Leadership roles - can access leadership-only features (Marketing Tracker, etc.)
const LEADERSHIP_ROLES = ['super_admin', 'admin', 'ceo', 'president', 'president_&_ceo', 'director_of_sales', 'director_of_marketing', 'marketing_manager', 'general_manager', 'leadership', 'director', 'vp'];

// Specific users who have Marketing Tracker access (by full name, case-insensitive)
const MARKETING_TRACKER_ALLOWED_USERS = [
  'ryan adams',
  'gabriel gecan'
];

// =====================================================
// ROLLOUT 1 CONFIGURATION
// =====================================================
// Features enabled for ALL users in first rollout:
// - Board
// - Leads & Projects  
// - Operations Hub
// - EOS Hub
// Remaining features still locked to admin only until Rollout 2
const ROLLOUT_1_ENABLED = true;

// Features still under progressive lockdown (not in Rollout 1)
const PROGRESSIVE_RELEASE_LOCKDOWN = true;

// Hubs/Features still in development - show info icon with tooltip
const IN_DEVELOPMENT_HUBS = [
  'operations-hub',
  'marketing-hub', 
  'call-center',
  'communications',
  'finance-hub',
  'report-builder',
  'canvas',
  'eos'
];

// In Development indicator component
const InDevelopmentBadge = ({ className = "" }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className={`h-3.5 w-3.5 text-amber-500 cursor-help ${className}`} />
      </TooltipTrigger>
      <TooltipContent 
        side="right" 
        className="max-w-[220px] bg-amber-50 dark:bg-amber-900/90 text-amber-900 dark:text-amber-100 border border-amber-200 dark:border-amber-700"
      >
        <p className="text-xs leading-relaxed">
          <span className="font-semibold">In Development:</span> Not all features, buttons, and connections are completed. Some data is mock information until the feature is completed.
        </p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Helper function to check if user is admin (has full platform access)
const isAdminUser = (userRole) => {
  return ADMIN_ROLES.includes(userRole);
};

// Helper function to check if user has leadership access
const isLeadershipUser = (userRole) => {
  return LEADERSHIP_ROLES.includes(userRole);
};

// Helper function to check if user has Marketing Tracker access (by name or role)
const hasMarketingTrackerAccess = (user) => {
  if (!user) return false;
  
  // Check by role first - admins and leadership can access
  const role = user.role || '';
  if (['super_admin', 'admin', 'ceo', 'president', 'president_&_ceo', 'marketing', 'marketing_manager', 'director_of_marketing', 'director_of_sales'].includes(role)) {
    return true;
  }
  
  // Fallback to name-based whitelist
  const userFullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().trim();
  return MARKETING_TRACKER_ALLOWED_USERS.includes(userFullName);
};

// Helper function to check if feature is in Rollout 1
const isRollout1Feature = (feature) => {
  if (!ROLLOUT_1_ENABLED) return false;
  const rollout1Features = ['board', 'leads_projects', 'operations_hub', 'eos_hub'];
  return rollout1Features.includes(feature);
};

// Helper function to determine which hub(s) a role can see
const canSeeHub = (userRole, hubType) => {
  // Admins, Business Ops, and Testers can see everything (testers need to test all flows)
  if (ADMIN_ROLES.includes(userRole) || BUSINESS_OPS_ROLES.includes(userRole) || TESTER_ROLES.includes(userRole)) return true;
  
  // ROLLOUT 1: Operations Hub available to all users
  if (hubType === 'operations' && isRollout1Feature('operations_hub')) {
    return true;
  }
  
  // PROGRESSIVE RELEASE: Other hubs locked for non-admin
  if (PROGRESSIVE_RELEASE_LOCKDOWN && !isAdminUser(userRole)) {
    return false;
  }
  
  // Department-specific access (only applies when lockdown is off)
  if (hubType === 'pipeline' || hubType === 'sales') {
    return SALES_ROLES.includes(userRole) || CALL_CENTER_ROLES.includes(userRole);
  }
  if (hubType === 'production' || hubType === 'operations') {
    return OPERATIONS_ROLES.includes(userRole);
  }
  if (hubType === 'marketing') {
    return MARKETING_ROLES.includes(userRole);
  }
  
  return false;
};

// Nav config is now imported from ../config/navigation.config.js
// This ensures nav renders identically even if pageMetadata fails

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isSuperAdmin, viewAsRole, viewAsUser, setTestingView, clearTestingView } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const ws = useWS();
  
  // Mobile detection for responsive navigation
  const { 
    showMobileNav, 
    isMobile, 
    isManualOverride, 
    toggleMobileMode,
    autoIsMobile 
  } = useMobileDetection();
  
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Load sidebar state from localStorage
    const saved = localStorage.getItem('sidebar_open');
    return saved !== null ? saved === 'true' : true;
  });
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    // Load pinned state from localStorage - default to false (collapsed)
    const saved = localStorage.getItem('sidebar_pinned');
    return saved !== null ? saved === 'true' : false;
  });
  const [roles, setRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [pageMetadata, setPageMetadata] = useState([]);
  const [buildInfo, setBuildInfo] = useState(null);
  const [lightModeEnabled, setLightModeEnabled] = useState(false);
  
  // Ref to preserve sidebar scroll position during navigation
  const navScrollRef = useRef(null);
  const savedScrollPosition = useRef(0);
  const hoverTimeoutRef = useRef(null);

  // Compute effective sidebar open state (pinned OR hovered)
  const effectiveSidebarOpen = sidebarPinned || sidebarHovered;

  // Sidebar hover handlers with small delay
  const handleSidebarMouseEnter = () => {
    if (!sidebarPinned) {
      // Clear any pending close timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      setSidebarHovered(true);
    }
  };

  const handleSidebarMouseLeave = () => {
    if (!sidebarPinned) {
      // Add small delay before closing to prevent flickering
      hoverTimeoutRef.current = setTimeout(() => {
        setSidebarHovered(false);
      }, 150);
    }
  };

  // Navigation helper that auto-collapses sidebar on desktop
  const navigateAndCollapse = (path) => {
    navigate(path);
    // Auto-collapse sidebar on navigation (desktop only) - only if not pinned
    if (!showMobileNav && sidebarPinned) {
      // Keep sidebar open if pinned, just navigate
    }
  };
  
  // Toggle sidebar pinned state with localStorage persistence
  const toggleSidebar = () => {
    const newState = !sidebarPinned;
    setSidebarPinned(newState);
    localStorage.setItem('sidebar_pinned', newState.toString());
    // Also update old key for compatibility
    localStorage.setItem('sidebar_open', newState.toString());
  };

  // Fetch company settings to check if light mode is enabled
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/company`);
        setLightModeEnabled(response.data.light_mode_enabled || false);
      } catch (error) {
        // Default to disabled if fetch fails
        setLightModeEnabled(false);
      }
    };
    fetchCompanySettings();
  }, []);

  // Save scroll position before any navigation
  const saveScrollPosition = () => {
    if (navScrollRef.current) {
      savedScrollPosition.current = navScrollRef.current.scrollTop;
    }
  };

  // Restore scroll position after navigation - use multiple methods to ensure it works
  useEffect(() => {
    const restoreScroll = () => {
      if (navScrollRef.current && savedScrollPosition.current > 0) {
        navScrollRef.current.scrollTop = savedScrollPosition.current;
      }
    };
    
    // Try multiple times to ensure scroll is restored after all renders
    restoreScroll();
    requestAnimationFrame(restoreScroll);
    const timer = setTimeout(restoreScroll, 50);
    
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  // Handle clicks within the nav to save scroll position before navigation
  const handleNavContainerClick = (e) => {
    // Save scroll position on any click within nav (before navigation happens)
    saveScrollPosition();
  };
  
  // Initialize expanded states from localStorage or based on current path
  const [platformModulesOpen, setPlatformModulesOpen] = useState(() => {
    const saved = localStorage.getItem('nav_platformModulesOpen');
    if (saved !== null) return JSON.parse(saved);
    // Auto-expand if on a path that's inside Platform Modules for super admin
    return location.pathname.startsWith('/call-center') || 
           location.pathname.startsWith('/sales-hub') || 
           location.pathname.startsWith('/operations-hub') ||
           location.pathname.startsWith('/marketing') ||
           location.pathname.startsWith('/eos');
  });
  const [platformEditorOpen, setPlatformEditorOpen] = useState(false);
  
  // Sales Hub
  const [salesHubOpen, setSalesHubOpen] = useState(() => {
    const saved = localStorage.getItem('nav_salesHubOpen');
    if (saved !== null) return JSON.parse(saved);
    // Auto-expand if on a sales-related path
    const salesPaths = ['/board', '/contacts', '/pipeline/', '/jobs'];
    const lastHub = localStorage.getItem('nav_lastClickedHub');
    if (location.pathname.startsWith('/jobs') && lastHub === 'sales') return true;
    return salesPaths.some(p => location.pathname.startsWith(p) || location.pathname === p);
  });
  
  // Operations Hub
  const [operationsHubOpen, setOperationsHubOpen] = useState(() => {
    const saved = localStorage.getItem('nav_operationsHubOpen');
    if (saved !== null) return JSON.parse(saved);
    // Auto-expand if on an operations-related path
    const operationsPaths = ['/operations-hub/', '/production/'];
    const lastHub = localStorage.getItem('nav_lastClickedHub');
    if (location.pathname.startsWith('/jobs') && lastHub === 'operations') return true;
    return operationsPaths.some(p => location.pathname.startsWith(p));
  });
  
  // Marketing Hub
  const [marketingHubOpen, setMarketingHubOpen] = useState(() => {
    const saved = localStorage.getItem('nav_marketingHubOpen');
    if (saved !== null) return JSON.parse(saved);
    return location.pathname.startsWith('/marketing-hub') || location.pathname.startsWith('/marketing/');
  });
  
  const [marketingDevHubOpen, setMarketingDevHubOpen] = useState(() => {
    const saved = localStorage.getItem('nav_marketingDevHubOpen');
    if (saved !== null) return JSON.parse(saved);
    return false;
  });
  
  const [callCenterHubOpen, setCallCenterHubOpen] = useState(() => {
    const saved = localStorage.getItem('nav_callCenterHubOpen');
    if (saved !== null) return JSON.parse(saved);
    return location.pathname.startsWith('/call-center');
  });
  const [callCenterSettingsOpen, setCallCenterSettingsOpen] = useState(() => {
    return location.pathname.startsWith('/call-center/settings');
  });
  const [communicationsOpen, setCommunicationsOpen] = useState(() => {
    const saved = localStorage.getItem('nav_communicationsOpen');
    if (saved !== null) return JSON.parse(saved);
    return location.pathname.startsWith('/communications');
  });
  const [eosHubOpen, setEosHubOpen] = useState(() => {
    const saved = localStorage.getItem('nav_eosHubOpen');
    if (saved !== null) return JSON.parse(saved);
    return location.pathname.startsWith('/eos');
  });
  const [dashboardsOpen, setDashboardsOpen] = useState(() => {
    const saved = localStorage.getItem('nav_dashboardsOpen');
    if (saved !== null) return JSON.parse(saved);
    return location.pathname.includes('dashboard') || 
           location.pathname === '/ceo/dashboard' ||
           location.pathname === '/director/dashboard' ||
           location.pathname === '/gm/dashboard' ||
           location.pathname === '/admin';
  });
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [ghostProtocolOpen, setGhostProtocolOpen] = useState(false);
  const [ghostSearchTerm, setGhostSearchTerm] = useState('');
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    profile_picture: user?.profile_picture || '',
  });
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    desktop_notifications: true,
    chat_notifications: true,
    mentions_only: false,
  });
  const [deadlineSettings, setDeadlineSettings] = useState({
    enabled: true,
    remind_7_days: true,
    remind_3_days: true,
    remind_1_day: true,
    remind_same_day: true,
    include_milestones: true,
    include_scope_items: true,
  });
  const [availability, setAvailability] = useState({
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '09:00', end: '17:00' },
    sunday: { enabled: false, start: '09:00', end: '17:00' },
  });
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [supportTicketDialogOpen, setSupportTicketDialogOpen] = useState(false);
  const [mediaHubOpen, setMediaHubOpen] = useState(false);
  const [supportTicketForm, setSupportTicketForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    screenshots: []
  });
  const [uploadingScreenshots, setUploadingScreenshots] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [chatNotifications, setChatNotifications] = useState([]);
  const [marketingNotifications, setMarketingNotifications] = useState([]);  // Phase 10
  const [deadlineNotifications, setDeadlineNotifications] = useState([]);  // Deadline reminders
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [marketingUnreadCount, setMarketingUnreadCount] = useState(0);  // Phase 10
  const [deadlineUnreadCount, setDeadlineUnreadCount] = useState(0);  // Deadline reminders
  const [communicationsUnreadCount, setCommunicationsUnreadCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [securitySettings, setSecuritySettings] = useState({
    settings_password_enabled: false,
    enterprise_password_enabled: false
  });
  
  // Password change form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // AI Assistant state
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantMessage, setAssistantMessage] = useState('');
  const [assistantMessages, setAssistantMessages] = useState([]);
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantSessionId, setAssistantSessionId] = useState(null);
  
  // Worklog modal state
  const [worklogModalOpen, setWorklogModalOpen] = useState(false);
  
  // Check if we're impersonating another user
  const [isImpersonating, setIsImpersonating] = useState(() => {
    return localStorage.getItem('is_impersonating') === 'true';
  });
  const [impersonatingAs, setImpersonatingAs] = useState(() => {
    return localStorage.getItem('impersonating_as') || '';
  });
  
  // Get the ghost protocol user (who initiated the viewing)
  const ghostProtocolUser = (() => {
    try {
      const stored = localStorage.getItem('ghost_protocol_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();
  
  // Determine if viewer is Ryan March (super admin) or a regular user with Ghost Protocol access
  const isAdminViewing = !ghostProtocolUser || ghostProtocolUser.isSuperAdmin;
  
  // Compute total unread notifications count (support + chat + marketing + deadlines)
  const totalUnreadNotifications = supportUnreadCount + chatUnreadCount + marketingUnreadCount + deadlineUnreadCount;
  
  // Combine all notifications for display
  const allNotifications = [
    ...notifications.map(n => ({ ...n, type: 'support' })),
    ...chatNotifications.map(n => ({ ...n, type: 'chat' })),
    ...marketingNotifications.map(n => ({ ...n, type: 'marketing' })),  // Phase 10
    ...deadlineNotifications.map(n => ({ ...n, type: 'deadline' }))  // Deadline reminders
  ].sort((a, b) => new Date(b.created_at || b.timestamp) - new Date(a.created_at || a.timestamp));
  
  // Format notification time
  const formatNotificationTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  // Mark all notifications as read
  const markAllNotificationsAsRead = async () => {
    try {
      // Mark support notifications
      if (notifications.length > 0) {
        for (const notif of notifications.filter(n => !n.read)) {
          await axios.post(`${API}/support-tickets/notifications/${notif.id}/mark-read`);
        }
      }
      // Mark chat notifications
      if (chatNotifications.length > 0) {
        await axios.post(`${API}/chat/mark-all-read`);
      }
      // Mark deadline notifications
      if (deadlineNotifications.length > 0) {
        await axios.post(`${API}/deadline-notifications/mark-all-read`);
      }
      fetchNotifications();
    } catch (err) {
      console.error('Error marking all notifications read:', err);
    }
  };
  
  // Handle notification click in dropdown
  const onNotificationItemClick = async (notif) => {
    // Mark as read
    if (!notif.read) {
      try {
        if (notif.type === 'support') {
          await axios.post(`${API}/support-tickets/notifications/${notif.id}/mark-read`);
        } else if (notif.type === 'chat') {
          await axios.post(`${API}/chat/messages/${notif.id}/read`);
        } else if (notif.type === 'deadline') {
          await axios.post(`${API}/deadline-notifications/${notif.id}/mark-read`);
        }
        fetchNotifications();
      } catch (err) {
        console.error('Error marking notification read:', err);
      }
    }
    
    // Navigate to related item
    if (notif.type === 'support' && notif.ticket_id) {
      navigate(`/my-tickets/${notif.ticket_id}`);
    } else if (notif.type === 'deadline') {
      navigate('/five-pillars');
    } else if (notif.type === 'chat') {
      navigate('/chat');
    } else if (notif.link) {
      navigate(notif.link);
    }
  };
  
  // Function to return to profile (either admin or the ghost protocol user's profile)
  const returnToProfile = async () => {
    try {
      if (isAdminViewing) {
        // Return to Ryan March's admin profile
        const adminToken = localStorage.getItem('admin_original_token');
        if (!adminToken) {
          toast.error('No admin session found. Please log in again.');
          navigate('/login');
          return;
        }
        
        // Restore admin token
        localStorage.setItem('access_token', adminToken);
        localStorage.removeItem('is_impersonating');
        localStorage.removeItem('impersonating_as');
        localStorage.removeItem('ghost_protocol_user');
        
        toast.success('Returning to Admin profile...');
        window.location.href = '/admin';
      } else {
        // Return to the ghost protocol user's profile
        const adminToken = localStorage.getItem('admin_original_token');
        if (!adminToken || !ghostProtocolUser) {
          toast.error('No session found. Please log in again.');
          navigate('/login');
          return;
        }
        
        // Impersonate back to the ghost protocol user
        const impersonateRes = await axios.post(
          `${API}/auth/impersonate`,
          { user_id: ghostProtocolUser.userId || ghostProtocolUser.id },
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
        
        localStorage.setItem('access_token', impersonateRes.data.access_token);
        localStorage.setItem('refresh_token', impersonateRes.data.refresh_token);
        localStorage.removeItem('is_impersonating');
        localStorage.removeItem('impersonating_as');
        localStorage.removeItem('ghost_protocol_user');
        
        toast.success(`Returning to ${ghostProtocolUser.firstName || ghostProtocolUser.first_name}'s profile...`);
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Error returning to profile:', error);
      toast.error('Failed to return to profile');
    }
  };
  
  // Ghost Protocol states for navbar protection
  const [navGhostModalOpen, setNavGhostModalOpen] = useState(false);
  const [pendingNavAction, setPendingNavAction] = useState(null);
  const [ghostModalConfig, setGhostModalConfig] = useState({
    title: 'Access Control Required',
    description: 'Enter your security password to continue.'
  });

  // Check if current user is Ryan March (always protected)
  const isRyanMarch = user?.email === 'rmarch@rhinoshieldflorida.com' || 
                      (user?.first_name === 'Ryan' && user?.last_name === 'March');
  
  // Check if user has ghost protection
  const hasGhostProtection = user?.ghost_login_protected && user?.ghost_login_password;
  
  // Determine if ghost protocol should be required for sensitive actions
  const requiresGhostProtection = isRyanMarch || hasGhostProtection;
  
  // Get the password for verification
  const ghostPassword = user?.ghost_login_password || MASTER_PASSWORD;

  // Handle ghost protocol protected navigation
  const handleProtectedNavigation = (action, path, config = {}) => {
    if (requiresGhostProtection) {
      setPendingNavAction({ action, path });
      setGhostModalConfig({
        title: config.title || 'Access Control Required',
        description: config.description || 'Enter your security password to access sensitive settings.'
      });
      setNavGhostModalOpen(true);
    } else {
      // No protection needed, execute action directly
      executeNavAction(action, path);
    }
  };

  const executeNavAction = (action, path) => {
    if (action === 'navigate') {
      navigate(path);
    } else if (action === 'openProfile') {
      setProfileDialogOpen(true);
    }
  };

  const handleNavGhostSuccess = () => {
    setNavGhostModalOpen(false);
    if (pendingNavAction) {
      executeNavAction(pendingNavAction.action, pendingNavAction.path);
      setPendingNavAction(null);
    }
  };

  const handleNavGhostCancel = () => {
    setNavGhostModalOpen(false);
    setPendingNavAction(null);
  };

  // Update profile form when user data changes
  useEffect(() => {
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        profile_picture: user.profile_picture || '',
      });
      setProfilePicturePreview(user.profile_picture || null);
      
      // Load user preferences if they exist
      if (user.notification_settings) {
        setNotificationSettings(user.notification_settings);
      }
      if (user.availability) {
        setAvailability(user.availability);
      }
      
      // Fetch deadline notification settings
      const fetchDeadlineSettings = async () => {
        try {
          const response = await axios.get(`${API}/deadline-notifications/settings`);
          if (response.data) {
            setDeadlineSettings(response.data);
          }
        } catch (e) {
          console.error('Error fetching deadline settings:', e);
        }
      };
      fetchDeadlineSettings();
    }
  }, [user]);

  // Determine effective user role (for "View As" feature)
  const getEffectiveUserRole = () => {
    if (viewAsUser && allUsers.length > 0) {
      const testUser = allUsers.find(u => u.id === viewAsUser);
      return testUser?.role || user?.role;
    }
    return user?.role;
  };

  const effectiveRole = getEffectiveUserRole();

  // Fetch roles and users for super admin and CEO (for Ghost Protocol)
  useEffect(() => {
    const canUseGhostProtocol = isSuperAdmin || 
                                 user?.role === 'ceo' || 
                                 user?.role === 'president' || 
                                 user?.role === 'president_&_ceo';
    if (canUseGhostProtocol) {
      fetchRoles();
      fetchUsers();
    }
  }, [isSuperAdmin, user?.role]);

  // Fetch page metadata for dynamic navigation
  useEffect(() => {
    fetchPageMetadata();
  }, []);

  // Fetch build info on mount
  useEffect(() => {
    const fetchBuildInfo = async () => {
      try {
        const response = await axios.get(`${API}/build-info`);
        setBuildInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch build info:', error);
      }
    };
    fetchBuildInfo();
  }, []);

  // Persist navigation expanded states to localStorage
  useEffect(() => {
    localStorage.setItem('nav_platformModulesOpen', JSON.stringify(platformModulesOpen));
  }, [platformModulesOpen]);

  useEffect(() => {
    localStorage.setItem('nav_salesHubOpen', JSON.stringify(salesHubOpen));
  }, [salesHubOpen]);

  useEffect(() => {
    localStorage.setItem('nav_operationsHubOpen', JSON.stringify(operationsHubOpen));
  }, [operationsHubOpen]);

  useEffect(() => {
    localStorage.setItem('nav_marketingHubOpen', JSON.stringify(marketingHubOpen));
  }, [marketingHubOpen]);

  useEffect(() => {
    localStorage.setItem('nav_marketingDevHubOpen', JSON.stringify(marketingDevHubOpen));
  }, [marketingDevHubOpen]);

  useEffect(() => {
    localStorage.setItem('nav_callCenterHubOpen', JSON.stringify(callCenterHubOpen));
  }, [callCenterHubOpen]);

  useEffect(() => {
    localStorage.setItem('nav_communicationsOpen', JSON.stringify(communicationsOpen));
  }, [communicationsOpen]);

  useEffect(() => {
    localStorage.setItem('nav_eosHubOpen', JSON.stringify(eosHubOpen));
  }, [eosHubOpen]);

  useEffect(() => {
    localStorage.setItem('nav_dashboardsOpen', JSON.stringify(dashboardsOpen));
  }, [dashboardsOpen]);

  // Restore filtered users when viewAsRole changes
  useEffect(() => {
    if (viewAsRole && viewAsRole !== 'super_admin' && roles.length > 0 && allUsers.length > 0) {
      const selectedRole = roles.find(r => r.id === viewAsRole);
      if (selectedRole) {
        const roleName = selectedRole.name;
        const usersWithRole = allUsers.filter(u => u.role === roleName);
        setFilteredUsers(usersWithRole);
      }
    }
  }, [viewAsRole, roles, allUsers]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchPageMetadata = async () => {
    try {
      const response = await axios.get(`${API}/pages`);
      setPageMetadata(response.data);
    } catch (error) {
      console.error('Failed to fetch page metadata:', error);
      // Fall back to empty array if pages can't be loaded
      setPageMetadata([]);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/users/${user.id}/profile-picture`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setProfileForm({ ...profileForm, profile_picture: response.data.profile_picture });
      toast.success('Profile picture uploaded');
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast.error('Failed to upload profile picture');
    }
  };

  const handleSaveProfile = async () => {
    try {
      const updateData = {
        ...profileForm,
        notification_settings: notificationSettings,
        availability: availability,
      };
      
      await axios.put(`${API}/users/${user.id}`, updateData);
      toast.success('Profile updated successfully');
      setProfileDialogOpen(false);
      // Optionally refetch user data
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    // Validate passwords
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    try {
      await axios.post(`${API}/users/${user.id}/change-password`, {
        new_password: passwordForm.new_password
      });
      
      toast.success('Password changed successfully');
      // Clear the form
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.detail;
      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage[0]?.msg || 'Failed to change password');
      } else {
        toast.error(errorMessage || 'Failed to change password');
      }
    }
  };

  const handleRoleChange = (roleId) => {
    if (roleId === 'super_admin') {
      clearTestingView();
      setFilteredUsers([]);
      toast.success('Viewing as Super Admin');
    } else {
      // Find the role name to filter users
      const selectedRole = roles.find(r => r.id === roleId);
      const roleName = selectedRole?.name;
      
      // Filter users by selected role
      const usersWithRole = allUsers.filter(u => u.role === roleName);
      setFilteredUsers(usersWithRole);
      
      // Update context with role (no user yet)
      setTestingView(roleId, '');
      
      toast.success(`Selected role: ${selectedRole?.display_name || selectedRole?.name}. Now select a user.`);
    }
  };

  const handleUserChange = (userId) => {
    const selectedUser = allUsers.find(u => u.id === userId);
    const selectedRole = roles.find(r => r.id === viewAsRole);
    
    if (selectedUser) {
      // Update context with both role and user
      setTestingView(viewAsRole, userId);
      toast.success(`Now viewing as: ${selectedUser.first_name} ${selectedUser.last_name} (${selectedRole?.display_name || selectedRole?.name})`);
    }
  };

  // Five Pillars access - only admins, CEO, and president can access
  const canAccessFivePillars = () => {
    if (!user) return false;
    const userRole = user.role?.toLowerCase() || '';
    const allowedRoles = ['super_admin', 'admin', 'ceo', 'president'];
    return allowedRoles.includes(userRole);
  };
  
  // Five Pillars edit permissions - only admins can edit
  const canEditFivePillars = () => {
    if (!user) return false;
    const userRole = user.role?.toLowerCase() || '';
    const editRoles = ['super_admin', 'admin'];
    return editRoles.includes(userRole);
  };

  // Ghost Protocol - Leadership names (case-insensitive matching)
  const leadershipNames = [
    'ryan adams',
    'gabriel gecan',
    'ryan march',
    'stephen creech',
    'lauren seibold'
  ];

  // Helper to check if user is in leadership
  const isLeadership = (ghostUser) => {
    const fullName = `${ghostUser.first_name} ${ghostUser.last_name}`.toLowerCase();
    return leadershipNames.some(name => fullName.includes(name) || name.includes(fullName));
  };

  // Map roles to department names for grouping
  const getRoleDepartment = (role) => {
    const roleMap = {
      'ceo': 'Executive',
      'president': 'Executive',
      'president_&_ceo': 'Executive',
      'super_admin': 'Administration',
      'admin': 'Administration',
      'general_manager': 'Management',
      'director_of_sales': 'Sales',
      'director_of_marketing': 'Marketing',
      'sales_manager': 'Sales',
      'sales': 'Sales',
      'call_center': 'Call Center',
      'call_center_manager': 'Call Center',
      'operations_manager': 'Operations',
      'production_manager': 'Production',
      'production': 'Production',
      'crew_lead': 'Production',
      'project_manager': 'Operations',
      'marketing': 'Marketing',
      'marketing_manager': 'Marketing'
    };
    return roleMap[role?.toLowerCase()] || 'Other';
  };

  // Format role for display
  const formatGhostRole = (role) => {
    if (!role) return 'N/A';
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Ghost Protocol - Filter users based on search term and deduplicate by email
  const ghostFilteredUsers = (() => {
    const seen = new Set();
    return allUsers.filter(u => {
      const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
      const email = u.email?.toLowerCase() || '';
      const search = ghostSearchTerm.toLowerCase();
      const matchesSearch = fullName.includes(search) || email.includes(search);
      
      // Deduplicate by email (keep first occurrence)
      if (matchesSearch && email && !seen.has(email)) {
        seen.add(email);
        return true;
      }
      return false;
    });
  })();

  // Organize ghost users: Leadership first, then by department
  const organizeGhostUsers = () => {
    const leadership = [];
    const departments = {};

    ghostFilteredUsers.forEach(ghostUser => {
      if (isLeadership(ghostUser)) {
        leadership.push(ghostUser);
      } else {
        const dept = getRoleDepartment(ghostUser.role);
        if (!departments[dept]) departments[dept] = [];
        departments[dept].push(ghostUser);
      }
    });

    // Sort leadership by the predefined order
    leadership.sort((a, b) => {
      const aName = `${a.first_name} ${a.last_name}`.toLowerCase();
      const bName = `${b.first_name} ${b.last_name}`.toLowerCase();
      const aIndex = leadershipNames.findIndex(name => aName.includes(name) || name.includes(aName));
      const bIndex = leadershipNames.findIndex(name => bName.includes(name) || name.includes(bName));
      return aIndex - bIndex;
    });

    // Define department order
    const deptOrder = ['Executive', 'Management', 'Administration', 'Sales', 'Call Center', 'Operations', 'Production', 'Marketing', 'Other'];
    const sortedDepts = Object.keys(departments).sort((a, b) => {
      return deptOrder.indexOf(a) - deptOrder.indexOf(b);
    });

    return { leadership, departments, sortedDepts };
  };

  // Ghost Protocol - Handle login as selected user
  const handleGhostLogin = async (ghostUser) => {
    try {
      // Get current admin token
      let adminToken = localStorage.getItem('admin_original_token') || 
                       localStorage.getItem('token') || 
                       localStorage.getItem('access_token');
      
      // Store original admin token before impersonating (if not already stored)
      if (!localStorage.getItem('admin_original_token')) {
        localStorage.setItem('admin_original_token', adminToken);
      }
      
      // Request impersonation token
      const impersonateRes = await axios.post(
        `${API}/auth/impersonate`,
        { user_id: ghostUser.id },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      const impersonateToken = impersonateRes.data.access_token;
      const impersonateRefreshToken = impersonateRes.data.refresh_token;
      
      // Store token and user data
      localStorage.setItem('access_token', impersonateToken);
      localStorage.setItem('refresh_token', impersonateRefreshToken);
      
      // Clear any "view as" settings
      localStorage.removeItem('viewAsRole');
      localStorage.removeItem('viewAsUser');
      
      setGhostProtocolOpen(false);
      toast.success(`Ghost login as ${ghostUser.first_name} ${ghostUser.last_name} (${ghostUser.role})`);
      
      // Role-based routing
      const role = ghostUser.role;
      let targetPath = '/dashboard';
      
      if (role === 'ceo' || role === 'president' || role === 'president_&_ceo') {
        targetPath = '/ceo/dashboard';
      } else if (role === 'super_admin' || role === 'admin') {
        targetPath = '/admin';
      } else if (role === 'director_of_sales') {
        targetPath = '/director/dashboard';
      } else if (role === 'general_manager') {
        targetPath = '/gm/dashboard';
      }
      
      // Store the target path before reload
      sessionStorage.setItem('impersonate_redirect', targetPath);
      
      // Reload to apply new auth context
      window.location.reload();
    } catch (error) {
      console.error('Error with ghost login:', error);
      toast.error('Failed to login as user');
    }
  };

  // Auto-expand hubs when on related pages
  // Uses getActiveHubForPath from navigation.config.js for deterministic behavior
  useEffect(() => {
    const lastHub = localStorage.getItem('nav_lastClickedHub');
    const activeHub = getActiveHubForPath(location.pathname, lastHub);
    
    // Expand the active hub based on current path
    if (activeHub === 'sales') {
      setSalesHubOpen(true);
    } else if (activeHub === 'operations') {
      setOperationsHubOpen(true);
    } else if (activeHub === 'marketing') {
      setMarketingHubOpen(true);
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleScreenshotUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingScreenshots(true);
    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/support-tickets/upload-screenshot`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedUrls.push(response.data.url);
      }
      
      setSupportTicketForm({
        ...supportTicketForm,
        screenshots: [...supportTicketForm.screenshots, ...uploadedUrls]
      });
      
      toast.success(`${files.length} screenshot(s) uploaded`);
    } catch (error) {
      console.error('Error uploading screenshots:', error);
      toast.error('Failed to upload screenshots');
    } finally {
      setUploadingScreenshots(false);
    }
  };

  const handleSubmitSupportTicket = async () => {
    if (!supportTicketForm.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    if (!supportTicketForm.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    try {
      const ticketData = {
        ...supportTicketForm,
        user_id: user.id,
        user_email: user.email,
        user_name: `${user.first_name} ${user.last_name}`,
        submitted_at: new Date().toISOString(),
        status: 'open'
      };

      await axios.post(`${API}/support-tickets`, ticketData);
      
      toast.success('Support ticket submitted successfully');
      setSupportTicketDialogOpen(false);
      
      // Reset form
      setSupportTicketForm({
        title: '',
        description: '',
        priority: 'medium',
        due_date: '',
        screenshots: []
      });
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      toast.error('Failed to submit support ticket');
    }
  };

  const removeScreenshot = (index) => {
    const newScreenshots = [...supportTicketForm.screenshots];
    newScreenshots.splice(index, 1);
    setSupportTicketForm({ ...supportTicketForm, screenshots: newScreenshots });
  };

  const fetchNotifications = async () => {
    // Fetch support notifications
    try {
      const supportResponse = await axios.get(`${API}/support-tickets/my-notifications`);
      setNotifications(supportResponse.data.notifications || []);
      setSupportUnreadCount(supportResponse.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching support notifications:', error);
      // Continue to fetch chat notifications even if support fails
    }
    
    // Fetch chat notifications independently
    try {
      const chatResponse = await axios.get(`${API}/chat/notifications`);
      setChatNotifications(chatResponse.data.notifications || []);
      setChatUnreadCount(chatResponse.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching chat notifications:', error);
    }
    
    // Phase 10: Fetch marketing notifications
    try {
      const marketingResponse = await axios.get(`${API}/notifications/marketing`);
      setMarketingNotifications(marketingResponse.data.notifications || []);
      setMarketingUnreadCount(marketingResponse.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching marketing notifications:', error);
    }
    
    // Fetch deadline notifications and generate any new ones
    try {
      // First generate any new notifications based on upcoming deadlines
      await axios.post(`${API}/deadline-notifications/generate`);
      // Then fetch all deadline notifications
      const deadlineResponse = await axios.get(`${API}/deadline-notifications`);
      setDeadlineNotifications(deadlineResponse.data.notifications || []);
      setDeadlineUnreadCount(deadlineResponse.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching deadline notifications:', error);
    }
    
    // Fetch communications unread count
    try {
      const commResponse = await axios.get(`${API}/communications/unread-count`);
      setCommunicationsUnreadCount(commResponse.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching communications unread count:', error);
    }
    
    // Fetch security settings for password protection checks
    try {
      const secResponse = await axios.get(`${API}/settings/security`);
      setSecuritySettings({
        settings_password_enabled: secResponse.data.settings_password_enabled ?? false,
        enterprise_password_enabled: secResponse.data.enterprise_password_enabled ?? false
      });
    } catch (error) {
      console.error('Error fetching security settings:', error);
    }
  };

  // Calculate total unread count (support + chat + communications + marketing + deadlines)
  useEffect(() => {
    setUnreadCount(supportUnreadCount + chatUnreadCount + communicationsUnreadCount + marketingUnreadCount + deadlineUnreadCount);
  }, [supportUnreadCount, chatUnreadCount, communicationsUnreadCount, marketingUnreadCount, deadlineUnreadCount]);

  const handleNotificationClick = async (notification) => {
    // Mark as read based on type
    try {
      if (notification.type === 'support') {
        await axios.post(`${API}/support-tickets/notifications/${notification.id}/mark-read`);
      } else if (notification.type === 'marketing') {
        await axios.post(`${API}/notifications/marketing/${notification.id}/mark-read`);
      } else if (notification.type === 'deadline') {
        await axios.post(`${API}/deadline-notifications/${notification.id}/mark-read`);
      }
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
    
    // Navigate based on notification type
    setNotificationDropdownOpen(false);
    
    if (notification.type === 'support') {
      navigate(`/my-tickets/${notification.ticket_id}`);
    } else if (notification.type === 'marketing') {
      // Navigate to the linked entity or marketing hub
      if (notification.link) {
        navigate(notification.link);
      } else {
        navigate('/marketing-hub/content-studio');
      }
    } else if (notification.type === 'deadline') {
      // Navigate to 5 Pillars Hub for deadline notifications
      navigate('/five-pillars');
    }
  };

  // AI Assistant send message
  const sendAssistantMessage = async () => {
    if (!assistantMessage.trim() || assistantLoading) return;
    
    const userMsg = { role: 'user', content: assistantMessage };
    setAssistantMessages(prev => [...prev, userMsg]);
    setAssistantMessage('');
    setAssistantLoading(true);
    
    try {
      const response = await axios.post(`${API}/platform-ai/assistant/chat`, {
        message: assistantMessage,
        session_id: assistantSessionId,
        user_id: user?.id,
        user_email: user?.email,
        department: user?.department,
        role: user?.role,
        context: {
          page: location.pathname,
          job_id: location.pathname.includes('/jobs/') ? location.pathname.split('/jobs/')[1]?.split('/')[0] : null,
          customer_id: location.pathname.includes('/contacts/') ? location.pathname.split('/contacts/')[1]?.split('/')[0] : null
        }
      });
      
      const aiMsg = { role: 'assistant', content: response.data.response };
      setAssistantMessages(prev => [...prev, aiMsg]);
      
      if (!assistantSessionId) {
        setAssistantSessionId(response.data.session_id);
      }
    } catch (error) {
      console.error('Assistant error:', error);
      const errorMsg = { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' };
      setAssistantMessages(prev => [...prev, errorMsg]);
    } finally {
      setAssistantLoading(false);
    }
  };

  const handleChatNotificationClick = async (notification) => {
    // Mark as read
    try {
      await axios.post(`${API}/chat/notifications/${notification.notification_id}/mark-read`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking chat notification as read:', error);
    }
    
    // Navigate to chat with the channel
    setNotificationDropdownOpen(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Mark all notifications as viewed
  const handleMarkAllAsViewed = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-viewed`);
      // Update local state immediately for better UX
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setChatNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setSupportUnreadCount(0);
      setChatUnreadCount(0);
      // Also fetch fresh data
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all notifications as viewed:', error);
    }
  };

  // Fetch notifications on mount and periodically
  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [user]);

  // Helper to get icon component from icon name
  const getIconComponent = (iconName) => {
    const iconMap = {
      LayoutDashboard,
      ShieldCheck,
      Palette,
      FileText,
      BarChart3,
      Trello,
      Users,
      Wrench,
      Megaphone,
      Settings
    };
    return iconMap[iconName] || FileText; // Default to FileText if icon not found
  };

  const isActive = (path) => {
    // Handle query params - match base path
    const basePath = path.split('?')[0];
    const currentBasePath = location.pathname;
    return currentBasePath === basePath || location.pathname === path;
  };
  
  // Hub active states - using getActiveHubForPath for consistency
  const lastClickedHub = localStorage.getItem('nav_lastClickedHub');
  const activeHub = getActiveHubForPath(location.pathname, lastClickedHub);
  
  const isSalesHubActive = activeHub === 'sales';
  const isOperationsHubActive = activeHub === 'operations';
  const isMarketingHubActive = activeHub === 'marketing';
  const isPlatformEditorActive = location.pathname.startsWith('/theme-editor') || location.pathname.startsWith('/page-manager') || location.pathname.startsWith('/content-manager') || location.pathname.startsWith('/branding-manager');
  const isDashboardActive = location.pathname.includes('dashboard') || 
                           location.pathname === '/ceo/dashboard' ||
                           location.pathname === '/director/dashboard' ||
                           location.pathname === '/gm/dashboard' ||
                           location.pathname === '/admin';

  // Helper to check if user can see a page (for pageMetadata enhancement only)
  // Nav renders from hardcoded config even if pageMetadata fails
  const canAccessPage = (page) => {
    // Page must be enabled
    if (!page.is_enabled) return false;
    
    // If no role restrictions, everyone can see it
    if (!page.allowed_roles || page.allowed_roles.length === 0) return true;
    
    // Check if user's role is in allowed roles
    const userRole = effectiveRole || user?.role;
    return page.allowed_roles.includes(userRole);
  };

  // Platform Editor tool paths (these go in Platform Editor dropdown, not main nav)
  const platformEditorPaths = ['/theme-editor', '/page-manager', '/content-manager', '/branding-manager'];

  // Paths to exclude from super admin navigation (to prevent duplicates)
  const excludedPaths = ['/dashboard', '/test-dynamic-content', '/developer-notes', '/admin'];

  // Build dynamic navigation from page metadata
  // Items to hide from call center users (they don't need these sales-focused items)
  const callCenterHiddenItems = ['My Jobs', 'Quote Estimates', 'Opportunities', 'Jobs'];
  
  const navigation = pageMetadata
    .filter(page => 
      !page.hub && // Root pages only
      canAccessPage(page) && // User has access
      !platformEditorPaths.includes(page.path) && // Exclude Platform Editor tools
      !excludedPaths.includes(page.path) && // Exclude specific paths for super admin
      // Hide certain items from call center users
      !(CALL_CENTER_ROLES.includes(effectiveRole) && callCenterHiddenItems.includes(page.title))
    )
    .sort((a, b) => a.order - b.order)
    .map(page => ({
      name: page.title,
      path: page.path,
      icon: getIconComponent(page.icon),
      show: true
    }));

  const platformEditorItems = [
    { name: 'Theme Editor', path: '/theme-editor', icon: Palette },
    { name: 'Page Manager', path: '/page-manager', icon: FileText },
    { name: 'Content Manager', path: '/content-manager', icon: LayoutDashboard },
    { name: 'Branding Manager', path: '/branding-manager', icon: Image },
  ];

  // Icon mapping for nav items
  const navIconMap = {
    'Trello': Trello,
    'Users': Users,
    'TrendingUp': TrendingUp,
    'FileText': FileText,
    'Briefcase': Briefcase,
    'Wrench': Wrench,
    'ClipboardList': ClipboardList,
    'CheckSquare': CheckSquare,
    'Megaphone': Megaphone,
    'Star': Star,
    'Target': Target,
    'MapPin': MapPin,
  };

  // Navigation click handler with hub context tracking
  const handleNavClick = (item, hubKey) => {
    // Store last clicked hub for duplicate path resolution (e.g., /jobs?view=sales vs /jobs?view=ops)
    if (hubKey) {
      localStorage.setItem('nav_lastClickedHub', hubKey);
    }
    
    // Handle coming soon items
    if (item.comingSoon) {
      toast.info(`${item.label} is coming soon!`);
      return;
    }
    
    // Save scroll position before navigating
    saveScrollPosition();
    navigate(item.path);
  };
  
  // Custom navigate function that preserves sidebar scroll position
  const navigatePreserveScroll = (path) => {
    saveScrollPosition();
    navigate(path);
  };

  // Render a single nav item with proper active state matching
  // Uses isNavItemActive from navigation.config.js for accurate query param matching
  const renderNavItem = (item, hubKey) => {
    // Check leadershipOnly permission - hide from non-leadership users
    // Special case: Marketing Tracker is also visible to specific whitelisted users
    if (item.leadershipOnly && !isLeadershipUser(effectiveRole)) {
      // Check if this is Marketing Tracker and user is in the whitelist
      if (item.id === 'marketing-tracker') {
        const userFullName = `${user?.first_name || ''} ${user?.last_name || ''}`.toLowerCase().trim();
        const isAllowedUser = MARKETING_TRACKER_ALLOWED_USERS.some(
          allowedName => userFullName === allowedName
        );
        if (!isAllowedUser) {
          return null;
        }
      } else {
        return null;
      }
    }
    
    const IconComponent = navIconMap[item.icon] || FileText;
    
    // Use the imported isNavItemActive function for accurate matching
    // This correctly handles /jobs?view=sales vs /jobs?view=ops
    const isItemActive = isNavItemActive(item, location.pathname, location.search);
    
    // For shared routes like /jobs, also check if this hub was the last clicked
    // This ensures the correct item stays highlighted after navigation
    const lastClickedHub = localStorage.getItem('nav_lastClickedHub');
    const isSharedRouteActive = item.sharedRoute && 
                                location.pathname.startsWith('/jobs') && 
                                lastClickedHub === hubKey;
    
    const shouldHighlight = isItemActive || isSharedRouteActive;
    
    return (
      <button
        key={item.id}
        onClick={() => handleNavClick(item, hubKey)}
        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-colors ${
          shouldHighlight
            ? 'bg-primary text-primary-foreground'
            : item.comingSoon
              ? 'text-muted-foreground/60 hover:bg-accent/50'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        }`}
        data-testid={`nav-item-${item.id}`}
      >
        <div className="flex items-center space-x-2">
          <IconComponent className="h-4 w-4" />
          <span>{item.label}</span>
        </div>
        {item.comingSoon && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">Soon</Badge>
        )}
        {item.badge && !item.comingSoon && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-500/10 text-blue-500 border-blue-500/30">{item.badge}</Badge>
        )}
      </button>
    );
  };

  // Call Center Hub items - Dashboard moved to Dashboards dropdown
  const callCenterHubItems = [
    { name: 'Control Panel', path: '/call-center/control-panel', badge: 'Live' },
    { name: 'Rhino-1 Voice', path: '/call-center/rhino-voice' },
    { name: 'Analytics', path: '/call-center/analytics' },
  ];
  
  // Call Center Settings sub-items (Admin/Manager only)
  const callCenterSettingsItems = [
    { name: 'Agents', path: '/call-center/settings?tab=agents' },
    { name: 'Queues', path: '/call-center/settings?tab=queues' },
    { name: 'Settings', path: '/call-center/settings?tab=settings' },
  ];

  // Check if user is a manager role
  const MANAGER_ROLES = ['sales_manager', 'operations_manager', 'production_manager', 'marketing_manager', 'call_center_manager', 'general_manager', 'director_of_sales'];
  const isManager = MANAGER_ROLES.includes(effectiveRole) || ADMIN_ROLES.includes(effectiveRole);

  // Bottom navigation - Settings only for admin roles
  const bottomNavigation = [
    { 
      name: 'Settings', 
      path: '/settings', 
      icon: Settings, 
      show: ADMIN_ROLES.includes(effectiveRole) || BUSINESS_OPS_ROLES.includes(effectiveRole)
    },
  ];

  const getInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`;
  };

  return (
    <div className={`min-h-screen bg-background ${showMobileNav ? 'pb-16' : ''}`}>
      {/* Sidebar - Hidden on mobile when mobile nav is active */}
      {!showMobileNav && (
      <aside
        className={`fixed top-0 left-0 z-40 h-screen transition-all duration-300 bg-card border-r border-border`}
        style={{ width: effectiveSidebarOpen ? '280px' : '64px' }}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`flex items-center ${effectiveSidebarOpen ? 'justify-center p-6' : 'justify-center p-3'} border-b border-border`}>
            {effectiveSidebarOpen ? (
              <img 
                src="/rsj-logo.svg" 
                alt="RhinoShield Logo" 
                className="h-12 w-auto"
                style={{
                  filter: isDark ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.8)) drop-shadow(0 0 16px rgba(59, 130, 246, 0.5)) drop-shadow(0 0 24px rgba(59, 130, 246, 0.3))' : 'none'
                }}
              />
            ) : (
              <img 
                src="/rsj-logo.svg" 
                alt="RS" 
                className="h-8 w-8 object-contain"
                style={{
                  filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.8))'
                }}
              />
            )}
          </div>

          {/* Navigation */}
          <nav 
            ref={navScrollRef} 
            onClick={handleNavContainerClick}
            className={`flex-1 ${effectiveSidebarOpen ? 'p-4' : 'p-2'} space-y-1 overflow-y-auto scrollbar-blue`}
          >
            {/* Collapsed state: Mirror exact expanded nav order */}
            {!effectiveSidebarOpen && (
              <div className="space-y-1">
                {/* Director of Sales specific items at top */}
                {!PROGRESSIVE_RELEASE_LOCKDOWN && effectiveRole === 'director_of_sales' && (
                  <>
                    <button
                      onClick={() => navigate('/director/dashboard')}
                      className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors ${
                        isActive('/director/dashboard') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                      }`}
                      title="Dashboard"
                    >
                      <LayoutDashboard className="h-5 w-5" />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors ${
                            location.pathname.startsWith('/call-center') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                          }`}
                          title="Call Center Hub"
                        >
                          <Phone className="h-5 w-5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent side="right" align="start" className="w-48">
                        <DropdownMenuLabel>Call Center Hub</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/call-center')}>
                          <LayoutDashboard className="h-4 w-4 mr-2" />
                          Lead Board
                        </DropdownMenuItem>
                        {callCenterHubItems.map(item => (
                          <DropdownMenuItem key={item.path} onClick={() => !item.disabled && navigate(item.path)} disabled={item.disabled}>
                            {item.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}

                {/* DEPLOYED Section - Dashboard */}
                <button
                  onClick={() => navigate('/dashboard')}
                  className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors ${
                    location.pathname === '/dashboard' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                  title="Dashboard"
                >
                  <LayoutDashboard className="h-5 w-5" />
                </button>

                {/* Board */}
                <button
                  onClick={() => navigate('/sales-hub/board')}
                  className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors ${
                    isActive('/sales-hub/board') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                  title="Board"
                >
                  <Trello className="h-5 w-5" />
                </button>

                {/* Leads & Projects */}
                <button
                  onClick={() => navigate('/workspace/records?source=sales&mode=leads')}
                  className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors relative ${
                    location.pathname === '/workspace/records' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                  title="Leads & Projects (In Development)"
                >
                  <Users className="h-5 w-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" title="In Development" />
                </button>

                {/* Operations Hub (Deployed) - Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors relative ${
                        isOperationsHubActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                      }`}
                      title="Operations Hub (In Development)"
                    >
                      <Wrench className="h-5 w-5" />
                      <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" title="In Development" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="right" align="start" className="w-52">
                    <DropdownMenuLabel className="flex items-center gap-2">
                      Operations Hub
                      <Info className="h-3 w-3 text-amber-500" />
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/operations-hub/dashboard')}>
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/operations-hub/capacity')}>
                      <Calendar className="h-4 w-4 mr-2" />
                      Capacity & Scheduling
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/operations-hub/revenue-projections')}>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Revenue Projections
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/operations-hub/project-map')}>
                      <MapPin className="h-4 w-4 mr-2" />
                      Project Map
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/operations-hub/crew')}>
                      <Users className="h-4 w-4 mr-2" />
                      Crew Schedule
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/operations-hub/production')}>
                      <Wrench className="h-4 w-4 mr-2" />
                      Production
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/operations-hub/work-orders')}>
                      <ClipboardList className="h-4 w-4 mr-2" />
                      Work Orders
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Marketing Hub (Deployed) - Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors relative ${
                        isMarketingHubActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                      }`}
                      title="Marketing Hub (In Development)"
                    >
                      <Megaphone className="h-5 w-5" />
                      <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" title="In Development" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="right" align="start" className="w-48">
                    <DropdownMenuLabel className="flex items-center gap-2">
                      Marketing Hub
                      <Info className="h-3 w-3 text-amber-500" />
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/marketing-hub/dashboard')}>
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/marketing-hub/tracker')}>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Marketing Tracker
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/marketing-hub/pacing')}>
                      <Target className="h-4 w-4 mr-2" />
                      On-Track Pacing
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/marketing-hub/ads-manager')}>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Ads Manager
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/marketing-hub/content-studio')}>
                      <FileText className="h-4 w-4 mr-2" />
                      Content Studio
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/marketing-hub/scheduler')}>
                      <Calendar className="h-4 w-4 mr-2" />
                      Scheduler
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/marketing-hub/assets')}>
                      <Image className="h-4 w-4 mr-2" />
                      Assets
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/marketing-hub/settings')}>
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Call Center (Deployed) - Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors relative ${
                        location.pathname.startsWith('/call-center') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                      }`}
                      title="Call Center (In Development)"
                    >
                      <Phone className="h-5 w-5" />
                      <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" title="In Development" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="right" align="start" className="w-48">
                    <DropdownMenuLabel className="flex items-center gap-2">
                      Call Center
                      <Info className="h-3 w-3 text-amber-500" />
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/call-center/control-panel')}>
                      <Settings className="h-4 w-4 mr-2" />
                      Control Panel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Communications (Deployed) */}
                <button
                  onClick={() => navigate('/communications')}
                  className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors relative ${
                    isActive('/communications') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                  title="Communications (In Development)"
                >
                  <MessageSquare className="h-5 w-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" title="In Development" />
                </button>

                {/* Finance Hub (Deployed) */}
                <button
                  onClick={() => navigate('/finance-hub')}
                  className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors relative ${
                    isActive('/finance-hub') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                  title="Finance Hub (In Development)"
                >
                  <DollarSign className="h-5 w-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" title="In Development" />
                </button>

                {/* Report Builder */}
                <button
                  onClick={() => navigate('/report-builder')}
                  className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors relative ${
                    isActive('/report-builder') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                  title="Report Builder (In Development)"
                >
                  <LayoutGrid className="h-5 w-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" title="In Development" />
                </button>

                {/* Canvas */}
                <button
                  onClick={() => navigate('/canvas')}
                  className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors relative ${
                    isActive('/canvas') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                  title="Canvas (In Development)"
                >
                  <Palette className="h-5 w-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" title="In Development" />
                </button>

                {/* EOS - Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors ${
                        location.pathname.startsWith('/eos') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                      }`}
                      title="EOS"
                    >
                      <Target className="h-5 w-5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent side="right" align="start" className="w-48">
                    <DropdownMenuLabel className="flex items-center gap-2">
                      EOS
                      <InDevelopmentBadge />
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/eos/dashboard')}>
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/eos/l10-meetings')}>
                      <Calendar className="h-4 w-4 mr-2" />
                      L10 Meetings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/eos/rocks')}>
                      <Target className="h-4 w-4 mr-2" />
                      Rocks
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/eos/scorecard')}>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Scorecard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/eos/issues')}>
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Issues
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/eos/todos')}>
                      <CheckSquare className="h-4 w-4 mr-2" />
                      To-Dos
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/eos/headlines')}>
                      <FileText className="h-4 w-4 mr-2" />
                      Headlines
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* 5 Pillars - Deployed (after EOS) */}
                {canAccessFivePillars() && (
                  <button
                    onClick={() => navigate('/five-pillars')}
                    className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors ${
                      isActive('/five-pillars') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                    }`}
                    title="5 Pillars"
                  >
                    <Shield className="h-5 w-5" />
                  </button>
                )}

                {/* IN DEVELOPMENT Section - Show only for super_admin */}
                {isSuperAdmin && (
                  <>
                    {/* Small divider */}
                    <div className="pt-2 mt-2 border-t border-border/50"></div>
                    
                    {/* Marketing Hub (Dev) - Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors text-muted-foreground hover:bg-accent`}
                          title="Marketing Hub (Dev)"
                        >
                          <Megaphone className="h-5 w-5 opacity-60" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent side="right" align="start" className="w-48">
                        <DropdownMenuLabel className="flex items-center gap-2">
                          Marketing Hub <span className="text-xs bg-blue-500/20 text-blue-400 px-1 rounded">Dev</span>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/marketing-hub')}>Dashboard</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}

                {/* Platform AI (for super_admin) */}
                {isSuperAdmin && (
                  <button
                    onClick={() => navigate('/platform-ai')}
                    className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors ${
                      isActive('/platform-ai') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                    }`}
                    title="Platform AI"
                  >
                    <Bot className="h-5 w-5" />
                  </button>
                )}

                {/* Expand button at bottom */}
                <div className="pt-4 border-t border-border mt-4">
                  <button
                    onClick={toggleSidebar}
                    className="w-full flex items-center justify-center p-3 rounded-lg text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                    title="Expand sidebar"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
            
            {/* Full navigation when expanded */}
            {effectiveSidebarOpen && (
              <>
            {/* Director of Sales - Standalone Dashboard at top */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && effectiveRole === 'director_of_sales' && (
              <button
                onClick={() => navigateAndCollapse('/director/dashboard')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/director/dashboard')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <LayoutDashboard className="h-5 w-5" />
                <span className="font-medium">Dashboard</span>
              </button>
            )}

            {/* Call Center Hub - For Director of Sales - RIGHT AFTER DASHBOARD */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && effectiveRole === 'director_of_sales' && (
              <div className="space-y-1">
                <button
                  onClick={() => setCallCenterHubOpen(!callCenterHubOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    location.pathname.startsWith('/call-center')
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5" />
                    <span className="font-medium">Call Center Hub</span>
                  </div>
                  {callCenterHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>

                {callCenterHubOpen && (
                  <div className="ml-8 space-y-1">
                    {/* Call Center Lead Board - ALWAYS first item inside hub */}
                    <button
                      onClick={() => navigateAndCollapse('/call-center')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/call-center') && !location.pathname.includes('/call-center/')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Lead Board</span>
                    </button>
                    {callCenterHubItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => !item.disabled && navigate(item.path)}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-colors ${
                          item.disabled 
                            ? 'text-muted-foreground/50 cursor-not-allowed'
                            : isActive(item.path)
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <span>{item.name}</span>
                        {item.badge === 'Live' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 rounded animate-pulse">
                            {item.badge}
                          </span>
                        )}
                      </button>
                    ))}
                    
                    {/* Settings Sub-dropdown - Director of Sales can access settings */}
                    <div className="space-y-1">
                      <button
                        onClick={() => setCallCenterSettingsOpen(!callCenterSettingsOpen)}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-colors ${
                          location.pathname.startsWith('/call-center/settings')
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Settings className="h-3 w-3" />
                          <span>Settings</span>
                        </div>
                        {callCenterSettingsOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      </button>
                      
                      {callCenterSettingsOpen && (
                        <div className="ml-4 space-y-1">
                          {callCenterSettingsItems.map((item) => (
                            <button
                              key={item.path}
                              onClick={() => navigate(item.path)}
                              className={`w-full flex items-center px-4 py-1.5 rounded-lg text-xs transition-colors ${
                                location.pathname + location.search === item.path
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              {item.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* DASHBOARD ARCHITECTURE RESET: Simplified Navigation */}
            
            {/* Define roles that see ONLY deployed features */}
            {(() => {
              const DEPLOYED_ONLY_ROLES = ['ceo', 'president', 'marketing_manager', 'director_of_sales', 'director_of_marketing'];
              const isDeployedOnlyUser = DEPLOYED_ONLY_ROLES.includes(effectiveRole);
              const showDeployedSection = isSuperAdmin || isDeployedOnlyUser;
              const showDevSection = isSuperAdmin && !isDeployedOnlyUser;
              
              return (
                <>
                  {/* ========== DEPLOYED FEATURES ========== */}
                  {showDeployedSection && (
                    <>
                      {/* Section Label - Only show for super_admin */}
                      {isSuperAdmin && (
                        <div className="px-4 py-2 mt-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">Deployed</span>
                            <div className="flex-1 h-px bg-green-600/20 dark:bg-green-400/20"></div>
                          </div>
                        </div>
                      )}

                      {/* Dashboard */}
                      <button
                        onClick={() => navigateAndCollapse('/dashboard')}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                          location.pathname === '/dashboard'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <LayoutDashboard className="h-5 w-5" />
                        <span className="font-medium">Dashboard</span>
                      </button>

                      {/* Board */}
                      <button
                        onClick={() => navigateAndCollapse('/sales-hub/board')}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive('/sales-hub/board')
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <Trello className="h-5 w-5" />
                        <span className="font-medium">Board</span>
                      </button>

                      {/* Leads & Projects */}
                      <button
                        onClick={() => navigateAndCollapse('/workspace/records?source=sales&mode=leads')}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                          location.pathname === '/workspace/records' && location.search.includes('source=sales')
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <Users className="h-5 w-5" />
                        <span className="font-medium">Leads & Projects</span>
                        <InDevelopmentBadge />
                      </button>

                      {/* Operations Hub (Deployed) */}
                      <div className="space-y-1">
                        <button
                          onClick={() => setOperationsHubOpen(!operationsHubOpen)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                            isOperationsHubActive
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Wrench className="h-5 w-5" />
                            <span className="font-medium">Operations Hub</span>
                            <InDevelopmentBadge />
                          </div>
                          {operationsHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        
                        {operationsHubOpen && (
                          <div className="ml-8 space-y-1">
                            <button
                              onClick={() => navigateAndCollapse('/operations-hub/dashboard')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/operations-hub/dashboard')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <LayoutDashboard className="h-4 w-4" />
                              <span>Dashboard</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/operations-hub/capacity')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/operations-hub/capacity')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Calendar className="h-4 w-4" />
                              <span>Capacity & Scheduling</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/operations-hub/revenue-projections')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/operations-hub/revenue-projections')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <TrendingUp className="h-4 w-4" />
                              <span>Revenue Projections</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/operations-hub/project-map')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/operations-hub/project-map')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <MapPin className="h-4 w-4" />
                              <span>Project Map</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/operations-hub/crew')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/operations-hub/crew')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Users className="h-4 w-4" />
                              <span>Crew Schedule</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/operations-hub/production')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/operations-hub/production')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Wrench className="h-4 w-4" />
                              <span>Production</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/operations-hub/work-orders')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/operations-hub/work-orders')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <ClipboardList className="h-4 w-4" />
                              <span>Work Orders</span>
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Marketing Hub (Deployed) */}
                      <div className="space-y-1">
                        <button
                          onClick={() => setMarketingHubOpen(!marketingHubOpen)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                            isMarketingHubActive
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Megaphone className="h-5 w-5" />
                            <span className="font-medium">Marketing Hub</span>
                            <InDevelopmentBadge />
                          </div>
                          {marketingHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        
                        {marketingHubOpen && (
                          <div className="ml-8 space-y-1">
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/dashboard')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/dashboard')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <LayoutDashboard className="h-4 w-4" />
                              <span>Dashboard</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/tracker')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/tracker')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <TrendingUp className="h-4 w-4" />
                              <span>Marketing Tracker</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/pacing')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/pacing')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Target className="h-4 w-4" />
                              <span>On-Track Pacing</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/ads-manager')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/ads-manager')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <BarChart3 className="h-4 w-4" />
                              <span>Ads Manager</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/content-studio')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/content-studio')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <FileText className="h-4 w-4" />
                              <span>Content Studio</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/scheduler')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/scheduler')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Calendar className="h-4 w-4" />
                              <span>Scheduler</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/assets')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/assets')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Image className="h-4 w-4" />
                              <span>Assets</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/settings')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/settings')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Settings className="h-4 w-4" />
                              <span>Settings</span>
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Call Center Hub (Deployed) */}
                      <div className="space-y-1">
                        <button
                          onClick={() => setCallCenterHubOpen(!callCenterHubOpen)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                            location.pathname.startsWith('/call-center')
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Phone className="h-5 w-5" />
                            <span className="font-medium">Call Center</span>
                            <InDevelopmentBadge />
                          </div>
                          {callCenterHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        
                        {callCenterHubOpen && (
                          <div className="ml-8 space-y-1">
                            <button
                              onClick={() => navigateAndCollapse('/call-center/control-panel')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/call-center/control-panel')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Settings className="h-4 w-4" />
                              <span>Control Panel</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/call-center/rhino-voice')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/call-center/rhino-voice')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Phone className="h-4 w-4" />
                              <span>Rhino-1 Voice</span>
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Communications (Deployed) */}
                      <button
                        onClick={() => navigateAndCollapse('/communications')}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive('/communications')
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <MessageSquare className="h-5 w-5" />
                        <span className="font-medium">Communications</span>
                        <InDevelopmentBadge />
                      </button>

                      {/* Finance Hub (Deployed) */}
                      <button
                        onClick={() => navigateAndCollapse('/finance-hub')}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive('/finance-hub')
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <DollarSign className="h-5 w-5" />
                        <span className="font-medium">Finance Hub</span>
                        <InDevelopmentBadge />
                      </button>

                      {/* Report Builder */}
                      <button
                        onClick={() => navigateAndCollapse('/report-builder')}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive('/report-builder')
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <LayoutGrid className="h-5 w-5" />
                        <span className="font-medium">Report Builder</span>
                        <InDevelopmentBadge />
                      </button>

                      {/* Canvas - Collaborative Whiteboard */}
                      <button
                        onClick={() => navigateAndCollapse('/canvas')}
                        data-testid="nav-canvas"
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive('/canvas')
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <Palette className="h-5 w-5" />
                        <span className="font-medium">Canvas</span>
                        <InDevelopmentBadge />
                      </button>

                      {/* EOS */}
                      <div className="space-y-1">
                        <button
                          onClick={() => setEosHubOpen(!eosHubOpen)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                            location.pathname.startsWith('/eos')
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Target className="h-5 w-5" />
                            <span className="font-medium">EOS</span>
                            <InDevelopmentBadge />
                          </div>
                          {eosHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        
                        {eosHubOpen && (
                          <div className="ml-8 space-y-1">
                            <button
                              onClick={() => navigateAndCollapse('/eos/dashboard')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/eos/dashboard')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <LayoutDashboard className="h-4 w-4" />
                              <span>Dashboard</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/eos/l10-meetings')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/eos/l10-meetings')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Calendar className="h-4 w-4" />
                              <span>L10 Meetings</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/eos/rocks')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/eos/rocks')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Target className="h-4 w-4" />
                              <span>Rocks</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/eos/scorecard')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/eos/scorecard')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <BarChart3 className="h-4 w-4" />
                              <span>Scorecard</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/eos/issues')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/eos/issues')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <AlertCircle className="h-4 w-4" />
                              <span>Issues (IDS)</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/eos/todos')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/eos/todos')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <CheckSquare className="h-4 w-4" />
                              <span>To-Dos</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/eos/people-analyzer')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/eos/people-analyzer')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Users className="h-4 w-4" />
                              <span>People Analyzer</span>
                            </button>
                          </div>
                        )}
                      </div>

                      {/* 5 Pillars Execution Hub */}
                      <button
                        onClick={() => navigateAndCollapse('/five-pillars')}
                        className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                          location.pathname === '/five-pillars' || location.pathname.startsWith('/five-pillars/')
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                        data-testid="nav-five-pillars"
                      >
                        <Shield className="h-5 w-5" />
                        <span className="font-medium">5 Pillars</span>
                      </button>
                    </>
                  )}

                  {/* ========== IN DEVELOPMENT (Super Admin Only) ========== */}
                  {isSuperAdmin && (
                    <>
                      {/* Section Label - In Development */}
                      <div className="px-4 py-2 mt-4">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">In Development</span>
                          <div className="flex-1 h-px bg-amber-600/20 dark:bg-amber-400/20"></div>
                        </div>
                      </div>

                      {/* Marketing Hub (Dev) - Pages still in development */}
                      <div className="space-y-1">
                        <button
                          onClick={() => setMarketingDevHubOpen(!marketingDevHubOpen)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                            marketingDevHubOpen
                              ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <Megaphone className="h-5 w-5" />
                            <span className="font-medium">Marketing Hub</span>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/50 text-amber-600 dark:text-amber-400">
                              Dev
                            </Badge>
                          </div>
                          {marketingDevHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        
                        {marketingDevHubOpen && (
                          <div className="ml-8 space-y-1">
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/check-ins')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/check-ins')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <MapPin className="h-4 w-4" />
                              <span>Check-Ins</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/campaigns')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/campaigns')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Megaphone className="h-4 w-4" />
                              <span>Campaigns</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/lead-sources')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/lead-sources')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Users className="h-4 w-4" />
                              <span>Lead Sources</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/analytics')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/analytics')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <BarChart3 className="h-4 w-4" />
                              <span>Analytics</span>
                            </button>
                            <button
                              onClick={() => navigateAndCollapse('/marketing-hub/agency')}
                              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive('/marketing-hub/agency')
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              <Building className="h-4 w-4" />
                              <span>Agency (SSW)</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              );
            })()}

            {/* NON-DEPLOYED ROLES: Other users keep their existing nav */}
            {(() => {
              const DEPLOYED_ONLY_ROLES = ['ceo', 'president', 'marketing_manager', 'director_of_sales', 'director_of_marketing', 'super_admin'];
              if (DEPLOYED_ONLY_ROLES.includes(effectiveRole)) return null;
              
              return (
                <>
                  {/* ROLLOUT 1: Board - Available to other users */}
                  {(isRollout1Feature('board')) && (
                    <button
                      onClick={() => navigateAndCollapse('/sales-hub/board')}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive('/sales-hub/board')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <Trello className="h-5 w-5" />
                      <span className="font-medium">Board</span>
                    </button>
                  )}

                  {/* ROLLOUT 1: Leads & Projects */}
                  {(isRollout1Feature('leads_projects')) && (
                    <button
                      onClick={() => navigateAndCollapse('/workspace/records?source=sales&mode=leads')}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                        location.pathname === '/workspace/records' && location.search.includes('source=sales')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <Users className="h-5 w-5" />
                      <span className="font-medium">Leads & Projects</span>
                    </button>
                  )}

                  {/* Marketing Tracker */}
                  {hasMarketingTrackerAccess(user) && (
                    <button
                      onClick={() => navigateAndCollapse('/marketing-hub/tracker')}
                      data-testid="nav-marketing-tracker"
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                        isActive('/marketing-hub/tracker')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <TrendingUp className="h-5 w-5" />
                        <span className="font-medium">Marketing Tracker</span>
                      </div>
                    </button>
                  )}
                  
                  {/* On-Track Pacing */}
                  {hasMarketingTrackerAccess(user) && (
                    <button
                      onClick={() => navigateAndCollapse('/marketing-hub/pacing')}
                      data-testid="nav-on-track-pacing"
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                        isActive('/marketing-hub/pacing')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Target className="h-5 w-5" />
                        <span className="font-medium">On-Track Pacing</span>
                      </div>
                    </button>
                  )}
                </>
              );
            })()}

            {/* CEO Dashboard - Direct link for CEO role */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && effectiveRole === 'ceo' && (
              <button
                onClick={() => navigateAndCollapse('/dashboard/ceo')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/dashboard/ceo')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Eye className="h-5 w-5" />
                <span className="font-medium">CEO Dashboard</span>
              </button>
            )}

            {/* President Dashboard - Direct link for President role */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && effectiveRole === 'president' && (
              <button
                onClick={() => navigateAndCollapse('/dashboard/president')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/dashboard/president')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <BarChart3 className="h-5 w-5" />
                <span className="font-medium">President Dashboard</span>
              </button>
            )}

            {/* President & CEO combo role */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && effectiveRole === 'president_&_ceo' && (
              <button
                onClick={() => navigateAndCollapse('/dashboard/ceo')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/dashboard/ceo') || isActive('/dashboard/president')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Eye className="h-5 w-5" />
                <span className="font-medium">Executive Dashboard</span>
              </button>
            )}

            {/* Other roles - show simple Dashboard link that goes to role-specific page */}
            {/* Only visible when lockdown is OFF */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && !['super_admin', 'ceo', 'president', 'president_&_ceo', 'director_of_sales'].includes(effectiveRole) && 
             !CALL_CENTER_ROLES.includes(effectiveRole) && (
              <button
                onClick={() => navigateAndCollapse('/dashboard')}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === '/dashboard'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <LayoutDashboard className="h-5 w-5" />
                <span className="font-medium">Dashboard</span>
              </button>
            )}

            {/* Other navigation items */}
            {navigation.filter(item => item.show).map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.name}</span>
                </button>
              );
            })}

            {/* Call Center Hub - For Call Center users - BEFORE EOS Hub */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && !isSuperAdmin && CALL_CENTER_ROLES.includes(effectiveRole) && (
              <div className="space-y-1">
                <button
                  onClick={() => setCallCenterHubOpen(!callCenterHubOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    location.pathname.startsWith('/call-center')
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Headphones className="h-5 w-5" />
                    <span className="font-medium">Call Center</span>
                  </div>
                  {callCenterHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                
                {callCenterHubOpen && (
                  <div className="ml-8 space-y-1">
                    {/* Call Center Lead Board - ALWAYS first item inside hub */}
                    <button
                      onClick={() => navigateAndCollapse('/call-center')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/call-center') && !location.pathname.includes('/call-center/')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Lead Board</span>
                    </button>
                    {callCenterHubItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => !item.disabled && navigate(item.path)}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-colors ${
                          item.disabled 
                            ? 'text-muted-foreground/50 cursor-not-allowed'
                            : isActive(item.path)
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <span>{item.name}</span>
                        {item.badge === 'Safe Mode' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 rounded">
                            {item.badge}
                          </span>
                        )}
                        {item.badge === 'Prep' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 rounded">
                            {item.badge}
                          </span>
                        )}
                        {item.badge === 'Live' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 rounded animate-pulse">
                            {item.badge}
                          </span>
                        )}
                      </button>
                    ))}
                    
                    {/* Settings Sub-dropdown - Admin/Manager Only */}
                    {(effectiveRole === 'super_admin' || effectiveRole === 'admin' || effectiveRole === 'call_center_manager') && (
                      <div className="space-y-1">
                        <button
                          onClick={() => setCallCenterSettingsOpen(!callCenterSettingsOpen)}
                          className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-colors ${
                            location.pathname.startsWith('/call-center/settings')
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Settings className="h-3 w-3" />
                            <span>Settings</span>
                          </div>
                          {callCenterSettingsOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        </button>
                        
                        {callCenterSettingsOpen && (
                          <div className="ml-4 space-y-1">
                            {callCenterSettingsItems.map((item) => (
                              <button
                                key={item.path}
                                onClick={() => navigate(item.path)}
                                className={`w-full flex items-center px-4 py-1.5 rounded-lg text-xs transition-colors ${
                                  location.pathname + location.search === item.path
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                }`}
                              >
                                {item.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Sales Hub - For non-super-admin users who can see sales - ABOVE EOS Hub */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && !isSuperAdmin && canSeeHub(effectiveRole, 'sales') && (
              <div className="space-y-1">
                <button
                  onClick={() => setSalesHubOpen(!salesHubOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isSalesHubActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Trello className="h-5 w-5" />
                    <span className="font-medium">Sales Hub</span>
                  </div>
                  {salesHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>

                {salesHubOpen && (
                  <div className="ml-8 space-y-1">
                    {/* Sales Hub Dashboard - ALWAYS first item inside hub */}
                    <button
                      onClick={() => navigateAndCollapse('/sales-hub/dashboard')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/sales-hub/dashboard')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Dashboard</span>
                    </button>
                    {PIPELINE_NAV_ITEMS.map((item) => renderNavItem(item, 'sales'))}
                  </div>
                )}
              </div>
            )}

            {/* ROLLOUT 1: Operations Hub - Available to other users (not deployed-only roles) */}
            {(() => {
              const DEPLOYED_ONLY_ROLES = ['ceo', 'president', 'marketing_manager', 'director_of_sales', 'director_of_marketing'];
              if (DEPLOYED_ONLY_ROLES.includes(effectiveRole)) return null;
              return (isSuperAdmin || isRollout1Feature('operations_hub')) && (
              <div className="space-y-1">
                <button
                  onClick={() => setOperationsHubOpen(!operationsHubOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isOperationsHubActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Wrench className="h-5 w-5" />
                    <span className="font-medium">Operations Hub</span>
                  </div>
                  {operationsHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                
                {operationsHubOpen && (
                  <div className="ml-8 space-y-1">
                    {PRODUCTION_NAV_ITEMS.map((item) => renderNavItem(item, 'operations'))}
                  </div>
                )}
              </div>
            );
            })()}

            {/* EOS Hub - Available to non-deployed-only users */}
            {(() => {
              const DEPLOYED_ONLY_ROLES = ['ceo', 'president', 'marketing_manager', 'director_of_sales', 'director_of_marketing'];
              if (DEPLOYED_ONLY_ROLES.includes(effectiveRole)) return null;
              return ((!PROGRESSIVE_RELEASE_LOCKDOWN || isRollout1Feature('eos_hub')) && !isSuperAdmin) && (
            <div className="space-y-1">
              <button
                onClick={() => setEosHubOpen(!eosHubOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                  location.pathname.startsWith('/eos') && !isActive('/eos/dashboard')
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Target className="h-5 w-5" />
                  <span className="font-medium">EOS</span>
                </div>
                {eosHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>

              {eosHubOpen && (
                <div className="ml-4 space-y-1 border-l-2 border-muted pl-4">
                  {/* EOS Dashboard - First item in EOS Hub */}
                  <button
                    onClick={() => navigateAndCollapse('/eos/dashboard')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/eos/dashboard')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>Dashboard</span>
                  </button>

                  <button
                    onClick={() => navigateAndCollapse('/eos/l10-meetings')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/eos/l10-meetings')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <Users className="h-4 w-4" />
                    <span>L10 Meetings</span>
                  </button>

                  <button
                    onClick={() => navigateAndCollapse('/eos/scorecard')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/eos/scorecard')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span>Scorecard</span>
                  </button>

                  <button
                    onClick={() => navigateAndCollapse('/eos/rocks')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/eos/rocks')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <Target className="h-4 w-4" />
                    <span>Rocks</span>
                  </button>

                  <button
                    onClick={() => navigateAndCollapse('/eos/issues')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/eos/issues')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <TrendingUp className="h-4 w-4" />
                    <span>Issues (IDS)</span>
                  </button>

                  <button
                    onClick={() => navigateAndCollapse('/eos/todos')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/eos/todos')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <Trello className="h-4 w-4" />
                    <span>To-Dos</span>
                  </button>

                  <button
                    onClick={() => navigateAndCollapse('/eos/headlines')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/eos/headlines')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <Megaphone className="h-4 w-4" />
                    <span>Headlines</span>
                  </button>

                  <button
                    onClick={() => navigateAndCollapse('/eos/accountability-chart')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/eos/accountability-chart')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <Users className="h-4 w-4" />
                    <span>Accountability</span>
                  </button>

                  <button
                    onClick={() => navigateAndCollapse('/eos/vto')}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive('/eos/vto')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <Target className="h-4 w-4" />
                    <span>V/TO</span>
                  </button>
                </div>
              )}
            </div>
            );
            })()}

            {/* Call Center Hub - Super Admin - NO LEAD BOARD */}
            {isSuperAdmin && (
              <div className="space-y-1">
                <button
                  onClick={() => setCallCenterHubOpen(!callCenterHubOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    location.pathname.startsWith('/call-center')
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Headphones className="h-5 w-5" />
                    <span className="font-medium">Call Center</span>
                  </div>
                  {callCenterHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                
                {callCenterHubOpen && (
                  <div className="ml-8 space-y-1">
                    {callCenterHubItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => !item.disabled && navigate(item.path)}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-colors ${
                          item.disabled 
                            ? 'text-muted-foreground/50 cursor-not-allowed'
                            : isActive(item.path)
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <span>{item.name}</span>
                        {item.badge === 'Live' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 rounded animate-pulse">
                            {item.badge}
                          </span>
                        )}
                      </button>
                    ))}
                    
                    {/* Settings Sub-dropdown for Super Admin */}
                    <div className="space-y-1 mt-1">
                      <button
                        onClick={() => setCallCenterSettingsOpen(!callCenterSettingsOpen)}
                        className={`w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm transition-colors ${
                          location.pathname.startsWith('/call-center/settings')
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Settings className="h-3 w-3" />
                          <span>Settings</span>
                        </div>
                        {callCenterSettingsOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      </button>
                      
                      {callCenterSettingsOpen && (
                        <div className="ml-4 space-y-1">
                          {callCenterSettingsItems.map((item) => (
                            <button
                              key={item.path}
                              onClick={() => navigate(item.path)}
                              className={`w-full flex items-center px-4 py-1.5 rounded-lg text-xs transition-colors ${
                                location.pathname + location.search === item.path
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              {item.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Communications - Super Admin */}
            {isSuperAdmin && (
              <div className="space-y-1">
                <button
                  onClick={() => setCommunicationsOpen(!communicationsOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    location.pathname.startsWith('/communications')
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <MessageSquare className="h-5 w-5" />
                    <span className="font-medium">Communications</span>
                    {communicationsUnreadCount > 0 && (
                      <Badge variant="destructive" className="h-5 px-2 text-xs">
                        {communicationsUnreadCount}
                      </Badge>
                    )}
                  </div>
                  {communicationsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                
                {communicationsOpen && (
                  <div className="ml-8 space-y-1">
                    <button
                      onClick={() => navigateAndCollapse('/communications')}
                      className={`w-full flex items-center px-4 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/communications') && !location.pathname.includes('/social-media')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      Messages
                    </button>
                    <button
                      onClick={() => navigateAndCollapse('/communications/social-media')}
                      className={`w-full flex items-center px-4 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/communications/social-media')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      Social Media
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Platform AI - Super Admin - Standalone */}
            {isSuperAdmin && (
              <button
                onClick={() => navigateAndCollapse('/platform-ai')}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                  isActive('/platform-ai')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Brain className="h-5 w-5" />
                  <span className="font-medium">Platform AI</span>
                </div>
                <Sparkles className="h-4 w-4 text-amber-500" />
              </button>
            )}

            {/* Automations - Super Admin - Standalone */}
            {isSuperAdmin && (
              <button
                onClick={() => navigateAndCollapse('/automations')}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                  isActive('/automations')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Cog className="h-5 w-5" />
                  <span className="font-medium">Automations</span>
                </div>
                <Sparkles className="h-4 w-4 text-blue-500" />
              </button>
            )}

            {/* Dashboards Dropdown - Super Admin - at bottom of nav */}
            {isSuperAdmin && (
              <div className="space-y-1">
                <button
                  onClick={() => setDashboardsOpen(!dashboardsOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isDashboardActive || isActive('/platform')
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <LayoutDashboard className="h-5 w-5" />
                    <span className="font-medium">Dashboards</span>
                  </div>
                  {dashboardsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>

                {dashboardsOpen && (
                  <div className="ml-8 space-y-1">
                    <button
                      onClick={() => navigateAndCollapse('/admin')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/admin')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Admin Dashboard</span>
                    </button>
                    {canAccessFivePillars() && (
                      <button
                        onClick={() => navigateAndCollapse('/governance/five-pillars')}
                        className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          isActive('/governance/five-pillars')
                            ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <Target className="h-4 w-4" />
                        <span>Five Pillars</span>
                      </button>
                    )}
                    <button
                      onClick={() => navigateAndCollapse('/platform')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/platform')
                          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <Rocket className="h-4 w-4" />
                      <span>Platform Dashboard</span>
                    </button>
                    <button
                      onClick={() => navigateAndCollapse('/operations-hub/dashboard')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/operations-hub/dashboard')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <Wrench className="h-4 w-4" />
                      <span>Operations Dashboard</span>
                    </button>
                    <button
                      onClick={() => navigateAndCollapse('/call-center/dashboards')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/call-center/dashboards')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <Headphones className="h-4 w-4" />
                      <span>Call Center Dashboard</span>
                    </button>
                    <button
                      onClick={() => navigateAndCollapse('/marketing-hub/dashboard')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/marketing-hub/dashboard')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <Megaphone className="h-4 w-4" />
                      <span>Marketing Dashboard</span>
                    </button>
                    <button
                      onClick={() => navigateAndCollapse('/sales-hub/dashboard')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/sales-hub/dashboard')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <Trello className="h-4 w-4" />
                      <span>Sales Dashboard</span>
                    </button>
                    <button
                      onClick={() => navigateAndCollapse('/dashboard/ceo')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/dashboard/ceo')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <Eye className="h-4 w-4" />
                      <span>CEO Dashboard</span>
                    </button>
                    <button
                      onClick={() => navigateAndCollapse('/director/dashboard')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/director/dashboard')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span>Director Dashboard</span>
                    </button>
                    <button
                      onClick={() => navigateAndCollapse('/gm/dashboard')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/gm/dashboard')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <Users className="h-4 w-4" />
                      <span>GM Dashboard</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Operations Hub - Hide for super admins (they have Platform Modules) */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && !isSuperAdmin && canSeeHub(effectiveRole, 'operations') && (
              <div className="space-y-1">
                <button
                  onClick={() => setOperationsHubOpen(!operationsHubOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isOperationsHubActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Wrench className="h-5 w-5" />
                    <span className="font-medium">Operations Hub</span>
                  </div>
                  {operationsHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>

                {operationsHubOpen && (
                  <div className="ml-8 space-y-1">
                    {/* Operations Hub Dashboard - ALWAYS first item inside hub */}
                    <button
                      onClick={() => navigateAndCollapse('/operations-hub/dashboard')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/operations-hub/dashboard')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Dashboard</span>
                    </button>
                    {/* P0 - Revenue Projections */}
                    <button
                      onClick={() => navigateAndCollapse('/operations-hub/revenue-projections')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/operations-hub/revenue-projections')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <TrendingUp className="h-4 w-4" />
                      <span>Revenue Projections</span>
                    </button>
                    {/* P0 - Project Map */}
                    <button
                      onClick={() => navigateAndCollapse('/operations-hub/project-map')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/operations-hub/project-map')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <MapPin className="h-4 w-4" />
                      <span>Project Map</span>
                    </button>
                    {PRODUCTION_NAV_ITEMS.map((item) => renderNavItem(item, 'operations'))}
                  </div>
                )}
              </div>
            )}

            {/* Marketing Hub - Hide for super admins (they have Platform Modules) */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && !isSuperAdmin && canSeeHub(effectiveRole, 'marketing') && (
              <div className="space-y-1">
                <button
                  onClick={() => setMarketingHubOpen(!marketingHubOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isMarketingHubActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Megaphone className="h-5 w-5" />
                    <span className="font-medium">Marketing</span>
                  </div>
                  {marketingHubOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>

                {marketingHubOpen && (
                  <div className="ml-8 space-y-1">
                    {/* Marketing Hub Dashboard - ALWAYS first item inside hub */}
                    <button
                      onClick={() => navigateAndCollapse('/marketing-hub/dashboard')}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActive('/marketing-hub/dashboard')
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Dashboard</span>
                    </button>
                    {MARKETING_NAV_ITEMS.map((item) => renderNavItem(item, 'marketing'))}
                  </div>
                )}
              </div>
            )}

            {/* Communications - Global for all users (non-super-admin, shown as standalone) */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && !isSuperAdmin && (
              <>
                <button
                  onClick={() => navigateAndCollapse('/communications')}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isActive('/communications') && !location.pathname.includes('/social-media')
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <MessageSquare className="h-5 w-5" />
                    <span className="font-medium">Communications</span>
                  </div>
                  {communicationsUnreadCount > 0 && (
                    <Badge variant="destructive" className="h-5 px-2 text-xs">
                      {communicationsUnreadCount}
                    </Badge>
                  )}
                </button>
                
                {/* Social Media - Only for marketing roles, not call center */}
                {MARKETING_ROLES.includes(effectiveRole) && (
                  <button
                    onClick={() => navigateAndCollapse('/communications/social-media')}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                      isActive('/communications/social-media')
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Share2 className="h-5 w-5" />
                      <span className="font-medium">Social Media</span>
                    </div>
                  </button>
                )}
              </>
            )}

            {/* Competition Zone - Available to all users (after Communications) */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && !isSuperAdmin && (
              <button
                onClick={() => navigateAndCollapse('/competition')}
                data-testid="nav-competition-zone"
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname.startsWith('/competition')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Trophy className="h-5 w-5" />
                <span className="font-medium">Competition Zone</span>
              </button>
            )}

            {/* My Team - For ALL Managers (after Competition Zone) */}
            {/* PROGRESSIVE RELEASE: Hidden during lockdown */}
            {!PROGRESSIVE_RELEASE_LOCKDOWN && isManager && !isSuperAdmin && (
              <button
                onClick={() => navigateAndCollapse('/my-team')}
                data-testid="nav-my-team"
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/my-team')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Users className="h-5 w-5" />
                <span className="font-medium">My Team</span>
              </button>
            )}

            {/* RHINO-1 Badge - At bottom of scrollable area */}
            <div className="flex items-center justify-center p-4 mt-4">
              <img 
                src="/assets/branding/rhino-1-badge-transparent.png"
                alt="RHINO-1 Platform Badge"
                className="w-full max-w-[180px] h-auto object-contain opacity-80 hover:opacity-100 transition-all duration-300"
                style={{
                  filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.8)) drop-shadow(0 0 16px rgba(59, 130, 246, 0.5)) drop-shadow(0 0 24px rgba(59, 130, 246, 0.3))'
                }}
              />
            </div>

            {/* Build Info Footer */}
            {buildInfo && (
              <div className="text-center px-4 pb-2">
                <p className="text-[10px] text-muted-foreground/60 font-mono">
                  Build: {buildInfo.commit} • {new Date(buildInfo.builtAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            )}
              </>
            )}
          </nav>

          {/* Bottom Navigation (Settings & Support) */}
          <div className={`${effectiveSidebarOpen ? 'p-4' : 'p-2'} border-t border-border space-y-2`}>
            {/* Admin Tools Dropdown - Super Admin Only */}
            {effectiveSidebarOpen && isSuperAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-300 ${
                      isActive('/super-admin') || isActive('/settings') || isActive('/support-hub')
                        ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white'
                        : 'bg-gradient-to-r from-blue-600/30 to-blue-500/30 text-blue-500 dark:text-blue-400 hover:from-blue-600/40 hover:to-blue-500/40 border-2 border-blue-500/60'
                    }`}
                    style={{
                      boxShadow: '0 0 15px rgba(59, 130, 246, 0.5), 0 0 30px rgba(59, 130, 246, 0.3)'
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <Shield className="h-5 w-5" />
                      <span className="font-medium">Admin Tools</span>
                    </div>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="start" 
                  className="w-56 bg-slate-900 border-2 border-blue-500/50 text-white"
                  style={{
                    boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 0 40px rgba(59, 130, 246, 0.15)'
                  }}
                >
                  <DropdownMenuLabel className="text-blue-400">Admin Tools</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-blue-500/30" />
                  
                  {/* Rhino-1 Enterprise */}
                  <DropdownMenuItem 
                    onClick={() => {
                      if (securitySettings.enterprise_password_enabled) {
                        handleProtectedNavigation('navigate', '/super-admin/agent-workforce', {
                          title: 'Rhino-1 Enterprise Access',
                          description: 'Enter your security password to access Rhino-1 Enterprise.'
                        });
                      } else {
                        navigate('/super-admin/agent-workforce');
                      }
                    }}
                    className="cursor-pointer hover:bg-blue-500/20 focus:bg-blue-500/20"
                  >
                    <Cpu className="mr-2 h-4 w-4 text-blue-400" />
                    <span>Rhino-1 Enterprise</span>
                    {securitySettings.enterprise_password_enabled && <Shield className="ml-auto h-3 w-3 text-amber-400" />}
                  </DropdownMenuItem>
                  
                  {/* Settings */}
                  <DropdownMenuItem 
                    onClick={() => {
                      if (securitySettings.settings_password_enabled) {
                        handleProtectedNavigation('navigate', '/settings', {
                          title: 'Settings Access',
                          description: 'Enter your security password to access settings.'
                        });
                      } else {
                        navigate('/settings');
                      }
                    }}
                    className="cursor-pointer hover:bg-blue-500/20 focus:bg-blue-500/20"
                  >
                    <Settings className="mr-2 h-4 w-4 text-blue-400" />
                    <span>Settings</span>
                    {securitySettings.settings_password_enabled && <Shield className="ml-auto h-3 w-3 text-amber-400" />}
                  </DropdownMenuItem>
                  
                  {/* Platform Settings (URLs, Integrations) */}
                  <DropdownMenuItem 
                    onClick={() => navigateAndCollapse('/admin/platform-settings')}
                    className="cursor-pointer hover:bg-blue-500/20 focus:bg-blue-500/20"
                  >
                    <Globe className="mr-2 h-4 w-4 text-blue-400" />
                    <span>Platform Settings</span>
                  </DropdownMenuItem>
                  
                  {/* Support Hub */}
                  <DropdownMenuItem 
                    onClick={() => navigateAndCollapse('/support-hub')}
                    className="cursor-pointer hover:bg-blue-500/20 focus:bg-blue-500/20"
                  >
                    <LifeBuoy className="mr-2 h-4 w-4 text-blue-400" />
                    <span>Support Hub</span>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator className="bg-blue-500/30" />
                  
                  {/* Ghost Protocol */}
                  <DropdownMenuItem 
                    onClick={() => setGhostProtocolOpen(true)}
                    className="cursor-pointer hover:bg-blue-500/20 focus:bg-blue-500/20"
                  >
                    <Ghost className="mr-2 h-4 w-4 text-blue-400" />
                    <span>Ghost Protocol</span>
                    <ArrowLeftRight className="ml-auto h-3 w-3 text-blue-400" />
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Chat - Available to all users */}
            {effectiveSidebarOpen ? (
              <button
                onClick={() => navigateAndCollapse('/chat')}
                data-testid="nav-chat"
                data-onboard="open-chat"
                className={`w-full flex items-center justify-between space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive('/chat')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <MessageSquare className="h-5 w-5" />
                  <span className="font-medium">Chat</span>
              </div>
              {ws && Object.values(ws.unreadCounts || {}).reduce((a, b) => a + b, 0) > 0 && (
                <Badge variant="destructive" className="h-5 px-2 text-xs">
                  {Object.values(ws.unreadCounts || {}).reduce((a, b) => a + b, 0)}
                </Badge>
              )}
            </button>
            ) : (
              <button
                onClick={() => navigate('/chat')}
                className={`w-full flex items-center justify-center p-3 rounded-lg transition-colors ${
                  isActive('/chat') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                }`}
                title="Chat"
              >
                <MessageSquare className="h-5 w-5" />
              </button>
            )}

            {/* Support Ticket - Available to all users */}
            {effectiveSidebarOpen ? (
              <button
                onClick={() => setSupportTicketDialogOpen(true)}
                data-testid="nav-support-ticket"
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-500/20 border border-yellow-500/20"
              >
                <LifeBuoy className="h-5 w-5" />
                <span className="font-medium">Support Ticket</span>
              </button>
            ) : (
              <button
                onClick={() => setSupportTicketDialogOpen(true)}
                className="w-full flex items-center justify-center p-3 rounded-lg transition-colors bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 hover:bg-yellow-500/20"
                title="Support Ticket"
              >
                <LifeBuoy className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Ghost Protocol Dialog */}
          <Dialog open={ghostProtocolOpen} onOpenChange={setGhostProtocolOpen}>
            <DialogContent 
              className="max-w-2xl max-h-[80vh] overflow-hidden bg-slate-900 border-2 border-blue-500 text-white"
              style={{
                boxShadow: '0 0 30px rgba(59, 130, 246, 0.4), 0 0 60px rgba(59, 130, 246, 0.2), 0 0 100px rgba(59, 130, 246, 0.1)'
              }}
            >
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-blue-400 flex items-center gap-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_data-dashboard-hub/artifacts/go2osn0g_Painter%20Rhino%20in%20Blue%20T-shirt.png" 
                    alt="Rhino" 
                    className="h-8 w-8 object-contain"
                  />
                  Ghost Protocol
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Search */}
                <Input
                  placeholder="Search users by name..."
                  value={ghostSearchTerm}
                  onChange={(e) => setGhostSearchTerm(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20"
                />
                
                {/* User List - Organized */}
                <div className="max-h-96 overflow-y-auto space-y-4 pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800">
                  {ghostFilteredUsers.length > 0 ? (
                    <>
                      {/* Leadership Section */}
                      {organizeGhostUsers().leadership.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2 sticky top-0 bg-slate-900 py-1">
                            <span className="text-yellow-400 text-lg">👑</span>
                            <h3 className="text-sm font-bold text-yellow-400 uppercase tracking-wider">Leadership</h3>
                            <div className="flex-1 h-px bg-yellow-400/30"></div>
                          </div>
                          <div className="space-y-2">
                            {organizeGhostUsers().leadership.map(ghostUser => (
                              <button
                                key={ghostUser.id}
                                onClick={() => handleGhostLogin(ghostUser)}
                                className="w-full p-3 text-left border border-yellow-500/30 rounded-lg bg-yellow-500/5 hover:bg-yellow-500/10 hover:border-yellow-500/50 transition-all duration-200 flex items-center gap-3 group"
                              >
                                <div className="h-10 w-10 rounded-full bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center group-hover:bg-yellow-500/30 transition-colors font-bold text-yellow-400">
                                  {ghostUser.employee_number || <User className="h-5 w-5 text-yellow-400" />}
                                </div>
                                <div className="flex-1 flex items-center justify-between">
                                  <div className="font-semibold text-white group-hover:text-yellow-300 transition-colors">
                                    {ghostUser.first_name} {ghostUser.last_name}
                                  </div>
                                  <div className="text-sm text-yellow-400/80 bg-yellow-500/10 px-2 py-1 rounded">
                                    {formatGhostRole(ghostUser.role)}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Department Sections */}
                      {organizeGhostUsers().sortedDepts.map(dept => (
                        <div key={dept}>
                          <div className="flex items-center gap-2 mb-2 sticky top-0 bg-slate-900 py-1">
                            <span className="text-blue-400 text-lg">
                              {dept === 'Sales' && '💼'}
                              {dept === 'Call Center' && '📞'}
                              {dept === 'Operations' && '⚙️'}
                              {dept === 'Production' && '🔧'}
                              {dept === 'Marketing' && '📣'}
                              {dept === 'Administration' && '🛡️'}
                              {dept === 'Executive' && '🏢'}
                              {dept === 'Management' && '📊'}
                              {dept === 'Other' && '👤'}
                            </span>
                            <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wider">{dept}</h3>
                            <div className="flex-1 h-px bg-blue-400/30"></div>
                          </div>
                          <div className="space-y-2">
                            {organizeGhostUsers().departments[dept].map(ghostUser => (
                              <button
                                key={ghostUser.id}
                                onClick={() => handleGhostLogin(ghostUser)}
                                className="w-full p-3 text-left border border-slate-700 rounded-lg bg-slate-800/50 hover:bg-slate-700 hover:border-blue-500/50 transition-all duration-200 flex items-center gap-3 group"
                              >
                                <div className="h-10 w-10 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors text-sm font-mono text-blue-400">
                                  {ghostUser.employee_number || <User className="h-5 w-5 text-blue-400" />}
                                </div>
                                <div className="flex-1 flex items-center justify-between">
                                  <div className="font-semibold text-white group-hover:text-blue-300 transition-colors">
                                    {ghostUser.first_name} {ghostUser.last_name}
                                  </div>
                                  <div className="text-sm text-blue-400/80 bg-blue-500/10 px-2 py-1 rounded">
                                    {formatGhostRole(ghostUser.role)}
                                  </div>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      {allUsers.length === 0 ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                          <span>Loading users...</span>
                        </div>
                      ) : 'No users found'}
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Pin/Unpin Sidebar Button */}
          <div className={`${effectiveSidebarOpen ? 'p-3' : 'p-2'} border-t border-border`}>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              className={`w-full flex items-center justify-center gap-2 ${sidebarPinned ? 'text-blue-500' : 'text-muted-foreground'}`}
              title={sidebarPinned ? "Unpin sidebar (hover to expand)" : "Pin sidebar open"}
            >
              {effectiveSidebarOpen ? (
                <>
                  {sidebarPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                  <span className="text-xs">{sidebarPinned ? 'Unpin' : 'Pin Open'}</span>
                </>
              ) : (
                sidebarPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />
              )}
            </Button>
          </div>

        </div>
      </aside>
      )}

      {/* Mobile Bottom Navigation */}
      {showMobileNav && <MobileBottomNav />}

      {/* Main Content */}
      <div
        className="transition-all duration-300"
        style={{ marginLeft: showMobileNav ? '0' : (effectiveSidebarOpen ? '280px' : '64px') }}
      >
        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between sticky top-0 z-50">
            <div className="flex items-center gap-2">
              <UserCog className="h-4 w-4" />
              <span className="text-sm font-medium">
                You are viewing as: <strong>{user?.first_name} {user?.last_name}</strong> ({user?.email})
              </span>
            </div>
            <Button 
              size="sm" 
              variant="secondary" 
              onClick={returnToProfile}
              className="h-7 text-xs bg-white text-amber-600 hover:bg-amber-50"
            >
              <ArrowLeftRight className="h-3 w-3 mr-1" />
              {isAdminViewing ? 'Return to Admin' : 'Return to Your Profile'}
            </Button>
          </div>
        )}
        
        {/* Header */}
        <header className={`sticky z-30 border-b border-border bg-card transition-colors ${isImpersonating ? 'top-[40px]' : 'top-0'}`}>
          <div className={`flex items-center justify-between py-4 ${showMobileNav ? 'px-4' : 'px-6'}`}>
            <div className="flex items-center space-x-4">
              {/* Sidebar toggle - only show on desktop */}
              {!showMobileNav && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleSidebar}
                  data-testid="sidebar-toggle"
                  title={sidebarPinned ? "Unpin sidebar" : "Pin sidebar open"}
                >
                  {sidebarPinned ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                </Button>
              )}
              {/* Mobile logo - only show on mobile */}
              {showMobileNav && (
                <img 
                  src="/rsj-logo.svg" 
                  alt="RhinoShield" 
                  className="h-8 w-auto"
                  style={{
                    filter: isDark ? 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))' : 'none'
                  }}
                />
              )}
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              {/* Mobile Mode Toggle - Quick access to switch between mobile/desktop mode */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMobileMode}
                title={
                  isManualOverride 
                    ? (showMobileNav ? 'Mobile mode ON (click for auto)' : 'Desktop mode ON (click for mobile)') 
                    : `Auto mode (${autoIsMobile ? 'mobile' : 'desktop'})`
                }
                className={`relative ${isManualOverride ? 'text-primary' : 'text-muted-foreground'}`}
                data-testid="mobile-mode-toggle"
              >
                {showMobileNav ? (
                  <Smartphone className="h-5 w-5" />
                ) : (
                  <Monitor className="h-5 w-5" />
                )}
                {isManualOverride && (
                  <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-primary rounded-full" />
                )}
              </Button>

              {/* 5 Pillars Update / Worklog Button - Leadership only */}
              <WorklogHeaderButton 
                currentUser={user} 
                onClick={() => setWorklogModalOpen(true)} 
              />

              {/* AI Assistant Button - Prime for Super Admin, Regular for others */}
              {/* PROGRESSIVE RELEASE: Hidden for non-admins during lockdown */}
              {(isSuperAdmin || !PROGRESSIVE_RELEASE_LOCKDOWN) && (
              <Button
                variant="ghost"
                size="icon"
                className="relative hidden sm:flex"
                title={isSuperAdmin ? "Rhino-1 Prime" : "AI Assistant"}
                onClick={() => setAssistantOpen(true)}
                data-onboard="open-ai-assistant"
              >
                {isSuperAdmin ? (
                  <>
                    <Crown className="h-5 w-5 text-amber-500" />
                    <Sparkles className="absolute -top-0.5 -right-0.5 h-3 w-3 text-amber-400" />
                  </>
                ) : (
                  <>
                    <Bot className="h-5 w-5 text-blue-500" />
                    <Sparkles className="absolute -top-0.5 -right-0.5 h-3 w-3 text-amber-500" />
                  </>
                )}
              </Button>
              )}

              {/* Notification Bell with Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="notification-bell-button"
                    className="relative hidden sm:flex"
                  >
                    <Bell className="h-5 w-5" />
                    {/* Unread notification badge */}
                    {totalUnreadNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {totalUnreadNotifications > 9 ? '9+' : totalUnreadNotifications}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  {/* Header */}
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <span className="font-semibold text-sm">Notifications</span>
                    <div className="flex items-center gap-2">
                      {totalUnreadNotifications > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={markAllNotificationsAsRead}
                        >
                          Mark all read
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => navigate('/settings/notifications')}
                        title="Notification Settings"
                      >
                        <Cog className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Notifications List */}
                  <div className="max-h-80 overflow-y-auto">
                    {allNotifications.length > 0 ? (
                      allNotifications.slice(0, 10).map((notif, idx) => (
                        <DropdownMenuItem
                          key={notif.id || idx}
                          className={`px-3 py-2 cursor-pointer ${!notif.read ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}
                          onClick={() => onNotificationItemClick(notif)}
                        >
                          <div className="flex items-start gap-3 w-full">
                            <div className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${
                              notif.type === 'deadline' 
                                ? notif.priority === 'critical' ? 'bg-red-500' 
                                  : notif.priority === 'high' ? 'bg-orange-500'
                                  : notif.priority === 'medium' ? 'bg-yellow-500'
                                  : 'bg-blue-500'
                                : !notif.read ? 'bg-blue-500' : 'bg-transparent'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                {notif.type === 'deadline' && <Clock className="h-3 w-3 text-orange-500" />}
                                <p className="text-sm font-medium truncate">{notif.title || notif.message || 'New notification'}</p>
                              </div>
                              <p className="text-xs text-muted-foreground truncate">{notif.message || notif.content || ''}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-xs text-muted-foreground">
                                  {formatNotificationTime(notif.created_at || notif.timestamp)}
                                </p>
                                {notif.type === 'deadline' && notif.source_type && (
                                  <Badge variant="outline" className="text-[9px] h-4 px-1">
                                    {notif.source_type === 'milestone' ? 'Milestone' : 'Feature'}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </DropdownMenuItem>
                      ))
                    ) : (
                      <div className="px-3 py-8 text-center">
                        <Bell className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                        <p className="text-sm text-muted-foreground">No notifications yet</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          You&apos;ll see notifications for tasks, notes, chat, and more
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Footer */}
                  {allNotifications.length > 10 && (
                    <div className="border-t px-3 py-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => navigate('/my-tickets')}
                      >
                        View all notifications
                      </Button>
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Notes - Global (visible to director_of_sales and admins) - hidden on mobile */}
              {(effectiveRole === 'director_of_sales' || ADMIN_ROLES.includes(effectiveRole)) && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/developer-notes')}
                  className={`hidden sm:flex ${isActive('/developer-notes') ? 'bg-accent' : ''}`}
                  title="Notes"
                >
                  <FileText className="h-5 w-5" />
                </Button>
              )}

              {/* Tasks - Global (available to all users) - NOW VISIBLE ON MOBILE */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/tasks')}
                className={isActive('/tasks') ? 'bg-accent' : ''}
                title="Tasks"
                data-testid="tasks-nav-btn"
              >
                <CheckSquare className="h-5 w-5" />
              </Button>

              {/* Calendar - Global (available to all users) - NOW VISIBLE ON MOBILE */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/calendar')}
                className={isActive('/calendar') ? 'bg-accent' : ''}
                title="Calendar"
                data-testid="calendar-nav-btn"
              >
                <Calendar className="h-5 w-5" />
              </Button>

              {/* Theme Toggle - Only show if light mode is enabled in settings - hidden on mobile */}
              {/* Theme Toggle - Always visible */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
                title={isDark ? "Switch to light mode" : "Switch to dark mode"}
                className="hidden sm:flex hover:bg-accent/80 transition-colors"
              >
                {isDark ? <Sun className="h-5 w-5 text-yellow-500" /> : <Moon className="h-5 w-5 text-slate-600" />}
              </Button>

              {/* Media Hub - Super Admin Only - hidden on mobile */}
              {isSuperAdmin && (
                <div className="relative hidden sm:block">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setMediaHubOpen(!mediaHubOpen)}
                    className={`relative ${mediaHubOpen ? 'bg-accent' : ''}`}
                    title="Media Hub"
                  >
                    <Headphones className={`h-5 w-5 ${mediaHubOpen ? 'text-blue-500' : ''}`} />
                  </Button>
                  <MediaHub 
                    isOpen={mediaHubOpen} 
                    onClose={() => setMediaHubOpen(false)}
                    isDark={isDark}
                  />
                </div>
              )}

              {/* User Profile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center space-x-2 p-2 rounded-lg hover:bg-accent transition-colors" data-testid="user-menu-trigger">
                    <Avatar className="h-8 w-8">
                      {user?.profile_picture && (
                        <AvatarImage 
                          src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_picture}`} 
                          alt={`${user.first_name} ${user.last_name}`} 
                        />
                      )}
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium hidden md:inline">{user?.first_name} {user?.last_name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* Return to Profile option - only show when impersonating */}
                  {isImpersonating && (
                    <>
                      <DropdownMenuItem onClick={returnToProfile} className="text-amber-600 dark:text-amber-400">
                        <ArrowLeftRight className="mr-2 h-4 w-4" />
                        <span>{isAdminViewing ? 'Return to Admin' : 'Return to Your Profile'}</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={() => handleProtectedNavigation('openProfile', null, {
                    title: 'Access Control Required',
                    description: 'Enter your security password to access profile settings.'
                  })}>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Profile Settings</span>
                    {requiresGhostProtection && <Shield className="ml-auto h-3 w-3 text-amber-500" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className={`${location.pathname === '/chat' ? '' : showMobileNav ? 'p-4' : 'p-6'}`}>
          {children}
        </main>

        {/* Footer with RHINO-1 Branding - hidden on mobile when bottom nav is visible */}
        {!showMobileNav && (
        <footer className="border-t border-border bg-card/50 py-3 px-6">
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <span>Powered by</span>
            <RhinoOneBadge size="xs" showTooltip={true} />
            <span className="font-medium text-foreground/80">RHINO-1</span>
            <span className="text-muted-foreground/50">•</span>
            <span>© {new Date().getFullYear()} RhinoShield Florida</span>
          </div>
        </footer>
        )}
      </div>

      {/* Profile Settings Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Profile Settings</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="security">
                <Lock className="h-4 w-4 mr-2" />
                Security
              </TabsTrigger>
              <TabsTrigger value="notifications">
                <Bell className="h-4 w-4 mr-2" />
                Notifications
              </TabsTrigger>
              <TabsTrigger value="availability">
                <Clock className="h-4 w-4 mr-2" />
                Availability
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-4 mt-4">
              {/* Profile Picture */}
              <div className="flex flex-col items-center space-y-4 pb-4 border-b">
                <Avatar className="h-24 w-24">
                  {profilePicturePreview ? (
                    <AvatarImage 
                      src={profilePicturePreview.startsWith('data:') 
                        ? profilePicturePreview 
                        : `${process.env.REACT_APP_BACKEND_URL}${profilePicturePreview}`
                      } 
                      alt="Profile" 
                      className="object-cover"
                    />
                  ) : null}
                  <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <Input
                    type="file"
                    id="profile-picture"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    className="hidden"
                  />
                  <Label htmlFor="profile-picture" className="cursor-pointer">
                    <Button variant="outline" size="sm" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Picture
                      </span>
                    </Button>
                  </Label>
                  <p className="text-xs text-muted-foreground mt-2">Max 5MB, JPG/PNG</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={profileForm.first_name}
                    onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                    placeholder="Enter first name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={profileForm.last_name}
                    onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                    placeholder="Enter last name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  placeholder="Enter email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>
              <div className="pt-2 border-t">
                <p className="text-sm text-muted-foreground">
                  <strong>Role:</strong> {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
              </div>
            </TabsContent>

            {/* Security Tab - Password Change */}
            <TabsContent value="security" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="pb-2 border-b">
                  <h3 className="font-medium">Change Password</h3>
                  <p className="text-sm text-muted-foreground">Update your password to keep your account secure</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    placeholder="Enter new password"
                  />
                  <p className="text-xs text-muted-foreground">Password must be at least 8 characters</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                  />
                </div>

                <Button 
                  onClick={handleChangePassword}
                  disabled={!passwordForm.new_password || !passwordForm.confirm_password}
                  className="w-full"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Update Password
                </Button>
              </div>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">Receive email updates for important events</p>
                  </div>
                  <Switch
                    checked={notificationSettings.email_notifications}
                    onCheckedChange={(checked) => 
                      setNotificationSettings({ ...notificationSettings, email_notifications: checked })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Desktop Notifications</Label>
                    <p className="text-sm text-muted-foreground">Show desktop alerts for new messages</p>
                  </div>
                  <Switch
                    checked={notificationSettings.desktop_notifications}
                    onCheckedChange={(checked) => 
                      setNotificationSettings({ ...notificationSettings, desktop_notifications: checked })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Chat Notifications</Label>
                    <p className="text-sm text-muted-foreground">Get notified for new chat messages</p>
                  </div>
                  <Switch
                    checked={notificationSettings.chat_notifications}
                    onCheckedChange={(checked) => 
                      setNotificationSettings({ ...notificationSettings, chat_notifications: checked })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Mentions Only</Label>
                    <p className="text-sm text-muted-foreground">Only notify when you are mentioned</p>
                  </div>
                  <Switch
                    checked={notificationSettings.mentions_only}
                    onCheckedChange={(checked) => 
                      setNotificationSettings({ ...notificationSettings, mentions_only: checked })
                    }
                  />
                </div>
                
                {/* Deadline Reminder Settings */}
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-orange-500" />
                    Deadline Reminders
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Enable Deadline Reminders</Label>
                        <p className="text-sm text-muted-foreground">Get notified about upcoming deadlines</p>
                      </div>
                      <Switch
                        checked={deadlineSettings.enabled}
                        onCheckedChange={async (checked) => {
                          setDeadlineSettings({ ...deadlineSettings, enabled: checked });
                          try {
                            await axios.patch(`${API}/deadline-notifications/settings`, { enabled: checked });
                          } catch (e) { console.error('Error updating deadline settings:', e); }
                        }}
                      />
                    </div>
                    {deadlineSettings.enabled && (
                      <>
                        <div className="pl-4 space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={deadlineSettings.remind_7_days}
                              onCheckedChange={async (checked) => {
                                setDeadlineSettings({ ...deadlineSettings, remind_7_days: checked });
                                try {
                                  await axios.patch(`${API}/deadline-notifications/settings`, { remind_7_days: checked });
                                } catch (e) { console.error(e); }
                              }}
                            />
                            <span>7 days before deadline</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={deadlineSettings.remind_3_days}
                              onCheckedChange={async (checked) => {
                                setDeadlineSettings({ ...deadlineSettings, remind_3_days: checked });
                                try {
                                  await axios.patch(`${API}/deadline-notifications/settings`, { remind_3_days: checked });
                                } catch (e) { console.error(e); }
                              }}
                            />
                            <span>3 days before deadline</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={deadlineSettings.remind_1_day}
                              onCheckedChange={async (checked) => {
                                setDeadlineSettings({ ...deadlineSettings, remind_1_day: checked });
                                try {
                                  await axios.patch(`${API}/deadline-notifications/settings`, { remind_1_day: checked });
                                } catch (e) { console.error(e); }
                              }}
                            />
                            <span>1 day before deadline</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={deadlineSettings.remind_same_day}
                              onCheckedChange={async (checked) => {
                                setDeadlineSettings({ ...deadlineSettings, remind_same_day: checked });
                                try {
                                  await axios.patch(`${API}/deadline-notifications/settings`, { remind_same_day: checked });
                                } catch (e) { console.error(e); }
                              }}
                            />
                            <span>On the day of deadline</span>
                          </div>
                        </div>
                        <div className="pl-4 pt-2 border-t space-y-2 text-sm">
                          <p className="text-xs text-muted-foreground mb-2">Include reminders for:</p>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={deadlineSettings.include_milestones}
                              onCheckedChange={async (checked) => {
                                setDeadlineSettings({ ...deadlineSettings, include_milestones: checked });
                                try {
                                  await axios.patch(`${API}/deadline-notifications/settings`, { include_milestones: checked });
                                } catch (e) { console.error(e); }
                              }}
                            />
                            <span>Timeline Milestones</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={deadlineSettings.include_scope_items}
                              onCheckedChange={async (checked) => {
                                setDeadlineSettings({ ...deadlineSettings, include_scope_items: checked });
                                try {
                                  await axios.patch(`${API}/deadline-notifications/settings`, { include_scope_items: checked });
                                } catch (e) { console.error(e); }
                              }}
                            />
                            <span>Scope Features</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Availability Tab */}
            <TabsContent value="availability" className="space-y-4 mt-4">
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Set your working hours for team chat availability (like Slack)
                </p>
                {Object.keys(availability).map((day) => (
                  <div key={day} className="flex items-center space-x-4 p-3 border rounded-lg">
                    <Checkbox
                      id={day}
                      checked={availability[day].enabled}
                      onCheckedChange={(checked) =>
                        setAvailability({
                          ...availability,
                          [day]: { ...availability[day], enabled: checked }
                        })
                      }
                    />
                    <Label htmlFor={day} className="flex-1 capitalize cursor-pointer">
                      {day}
                    </Label>
                    {availability[day].enabled && (
                      <div className="flex items-center space-x-2">
                        <Input
                          type="time"
                          value={availability[day].start}
                          onChange={(e) =>
                            setAvailability({
                              ...availability,
                              [day]: { ...availability[day], start: e.target.value }
                            })
                          }
                          className="w-32"
                        />
                        <span className="text-sm text-muted-foreground">to</span>
                        <Input
                          type="time"
                          value={availability[day].end}
                          onChange={(e) =>
                            setAvailability({
                              ...availability,
                              [day]: { ...availability[day], end: e.target.value }
                            })
                          }
                          className="w-32"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveProfile}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Support Ticket Dialog */}
      <Dialog open={supportTicketDialogOpen} onOpenChange={setSupportTicketDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submit Support Ticket</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Having an issue with the platform? Let us know and we will help you out.
            </p>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div>
              <Label htmlFor="ticket-title">Title *</Label>
              <Input
                id="ticket-title"
                placeholder="Brief description of the issue"
                value={supportTicketForm.title}
                onChange={(e) => setSupportTicketForm({ ...supportTicketForm, title: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="ticket-description">Description *</Label>
              <Textarea
                id="ticket-description"
                placeholder="Provide detailed information about the issue you're experiencing..."
                value={supportTicketForm.description}
                onChange={(e) => setSupportTicketForm({ ...supportTicketForm, description: e.target.value })}
                rows={6}
                className="mt-1"
              />
            </div>

            {/* Priority and Due Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ticket-priority">Priority</Label>
                <Select
                  value={supportTicketForm.priority}
                  onValueChange={(value) => setSupportTicketForm({ ...supportTicketForm, priority: value })}
                >
                  <SelectTrigger id="ticket-priority" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="ticket-due-date">Requested Resolution Date</Label>
                <Input
                  id="ticket-due-date"
                  type="date"
                  value={supportTicketForm.due_date}
                  onChange={(e) => setSupportTicketForm({ ...supportTicketForm, due_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Screenshot Upload */}
            <div>
              <Label>Screenshots (Optional)</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Upload screenshots of errors or issues
              </p>
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={handleScreenshotUpload}
                disabled={uploadingScreenshots}
                className="mt-1"
              />
              {uploadingScreenshots && (
                <p className="text-xs text-muted-foreground mt-2">Uploading...</p>
              )}

              {/* Display uploaded screenshots */}
              {supportTicketForm.screenshots.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-medium">Uploaded Screenshots:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {supportTicketForm.screenshots.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={`${BACKEND_URL}${url}`}
                          alt={`Screenshot ${index + 1}`}
                          className="w-full h-24 object-cover rounded border"
                        />
                        <button
                          onClick={() => removeScreenshot(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* User Info (read-only) */}
            <div className="bg-muted p-3 rounded-lg space-y-1">
              <p className="text-xs text-muted-foreground">Submitted by:</p>
              <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSupportTicketDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitSupportTicket}>
              Submit Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Assistant - Prime for Super Admin, Regular Sheet for others */}
      {isSuperAdmin ? (
        <PrimeAssistant 
          open={assistantOpen} 
          onClose={() => setAssistantOpen(false)} 
          user={user}
        />
      ) : (
        <Sheet open={assistantOpen} onOpenChange={setAssistantOpen}>
          <SheetContent className="w-[400px] sm:w-[450px] flex flex-col">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                Rhino-1 AI Assistant
              </SheetTitle>
              <SheetDescription>
                Ask me anything about jobs, customers, or company processes.
              </SheetDescription>
            </SheetHeader>
            
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {assistantMessages.length === 0 && (
                <div className="text-center py-8">
                  <Sparkles className="h-12 w-12 mx-auto mb-3 text-amber-500 opacity-50" />
                  <p className="text-muted-foreground text-sm">
                    Hi! I am your AI assistant. I can help you with:
                  </p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                    <li>• Job status and details</li>
                    <li>• Customer information</li>
                    <li>• Company processes</li>
                    <li>• Quick answers</li>
                  </ul>
                </div>
              )}
              
              {assistantMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-md'
                        : 'bg-slate-100 dark:bg-slate-800 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              
              {assistantLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-bl-md px-4 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}
            </div>
            
            {/* Input */}
            <div className="border-t pt-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Ask me anything..."
                  value={assistantMessage}
                  onChange={(e) => setAssistantMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendAssistantMessage();
                    }
                  }}
                  disabled={assistantLoading}
                />
                <Button
                  size="icon"
                  onClick={sendAssistantMessage}
                  disabled={!assistantMessage.trim() || assistantLoading}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Powered by GPT-4o • Press Enter to send
              </p>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Ghost Protocol Modal for Navbar Protection */}
      <GhostProtocolModal
        isOpen={navGhostModalOpen}
        onClose={handleNavGhostCancel}
        onSuccess={handleNavGhostSuccess}
        userName={user ? `${user.first_name} ${user.last_name}` : 'User'}
        requiredPassword={ghostPassword}
        title={ghostModalConfig.title}
        description={ghostModalConfig.description}
      />

      {/* Worklog Modal for Business Ops */}
      <WorklogModal
        isOpen={worklogModalOpen}
        onClose={() => setWorklogModalOpen(false)}
        currentUser={user}
      />
    </div>
  );
};